import { ImgWithFallback } from "@/components/img-with-fallback";
import { Component, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { SearchableSelect } from "@/lib/picker-search";
import { useToast } from "@/hooks/use-toast";
import type { Brand, Persona, PersonaLabFlow } from "@shared/schema";
import {
  FlaskConical, Loader2, AlertTriangle, ChevronDown, RefreshCw,
  CheckCircle2, CircleDashed, Copy, Download, Trash2, Play, Code2,
  Building2, UserRound, Target, MessageSquare, Quote,
  Microscope, Users, Compass, FileText, ListChecks,
  ShieldAlert, Eye, Database, Briefcase, ExternalLink, Lightbulb,
  Info, ThumbsUp, Ban, Sparkles, Save, ArrowLeft,
  UserSearch, ClipboardCheck, UserCheck,
  Server, Globe, Cpu, Wrench, ShieldCheck,
  ScanSearch, Network,
} from "lucide-react";

// ─── State model ────────────────────────────────────────────────────────
type ModuleStatus = "idle" | "running" | "complete" | "error";

interface LabState {
  selectedBrandId: string;
  selectedPersonaId: string;
  results: Record<string, any>;
  rawResults: Record<string, any>;
  status: Record<string, ModuleStatus>;
  errors: Record<string, string>;
}

const STORAGE_KEY = "personaResearchLabState";

function makeInitialState(brandId = ""): LabState {
  return {
    selectedBrandId: brandId,
    selectedPersonaId: "",
    results: {},
    rawResults: {},
    status: {},
    errors: {},
  };
}

function loadState(brandId = ""): LabState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return makeInitialState(brandId);
    const parsed = JSON.parse(raw);
    return {
      // Brand is fixed by the route; always trust the current brand.
      selectedBrandId: brandId || parsed.selectedBrandId || "",
      selectedPersonaId: parsed.selectedBrandId === brandId ? (parsed.selectedPersonaId || "") : "",
      results: parsed.selectedBrandId === brandId ? (parsed.results ?? {}) : {},
      rawResults: parsed.selectedBrandId === brandId ? (parsed.rawResults ?? {}) : {},
      status: parsed.selectedBrandId === brandId ? (parsed.status ?? {}) : {},
      errors: parsed.selectedBrandId === brandId ? (parsed.errors ?? {}) : {},
    };
  } catch {
    return makeInitialState(brandId);
  }
}

// ─── Payload normalization helpers ──────────────────────────────────────
// We keep raw selected brand/persona/product records in frontend state for
// display, but never send those raw database records to Sim. Instead we send
// compact, clean context objects that strip duplicated fields, AI execution
// logs, model/token/cost metadata, providerTiming, image URLs, and nested
// aiResearchResult objects — keeping only the useful business context.

function safeArray(value: any): any[] {
  return Array.isArray(value) ? value : [];
}

function firstNonEmpty(...values: any[]) {
  return values.find((v) => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim().length > 0;
    return v !== null && v !== undefined;
  });
}

function parseFingerprintContent(persona: any) {
  const content = persona?.fingerprint?.output?.content;
  if (!content || typeof content !== "string") {
    return persona?.fingerprint?.output?.persona_fingerprint
      ? persona.fingerprint.output
      : null;
  }
  try {
    const cleaned = content
      .replace(/^```json\s*/i, "")
      .replace(/^```\s*/i, "")
      .replace(/```$/i, "")
      .trim();
    return JSON.parse(cleaned);
  } catch {
    return content;
  }
}

function buildCompactBrandContext(brand: any) {
  if (!brand) return null;
  const enhanced = brand?.aiResearchResult?.enhanced || {};
  return {
    id: brand.id,
    name: brand.name,
    url: brand.url,
    category: firstNonEmpty(brand.category, enhanced.category),
    positioning: firstNonEmpty(brand.positioning, enhanced.positioning),
    promise: firstNonEmpty(brand.promise, enhanced.promise),
    mission: firstNonEmpty(brand.mission, enhanced.mission),
    enemy: firstNonEmpty(brand.enemy, enhanced.enemy),
    worldview: firstNonEmpty(brand.worldview, enhanced.worldview),
    tone: firstNonEmpty(brand.toneSummary, enhanced.toneSummary),
    key_products: firstNonEmpty(brand.keyProducts, enhanced.keyProducts, []),
    proof_hierarchy: firstNonEmpty(brand.proofHierarchy, enhanced.proofHierarchy, []),
    category_language: firstNonEmpty(brand.categoryLanguage, enhanced.categoryLanguage, []),
    price_positioning: firstNonEmpty(brand.pricePositioning, enhanced.pricePositioning),
    target_audience_signals: firstNonEmpty(brand.targetAudienceSignals, enhanced.targetAudienceSignals, []),
    social_profiles: firstNonEmpty(brand.socialProfiles, enhanced.socialProfiles, []),
    research_sources: firstNonEmpty(brand.researchSources, enhanced.researchSources, []),
    research_notes: firstNonEmpty(brand.researchNotes, enhanced.researchNotes),
    claim_guardrails: firstNonEmpty(brand.claimGuardrails, enhanced.claimGuardrails, []),
    approved_phrases: firstNonEmpty(brand.approvedPhrases, enhanced.approvedPhrases, []),
    banned_phrases: firstNonEmpty(brand.bannedPhrases, enhanced.bannedPhrases, []),
  };
}

function buildCompactPersonaContext(persona: any) {
  if (!persona) return null;
  const parsedFingerprint = parseFingerprintContent(persona);
  const fingerprint =
    parsedFingerprint?.persona_fingerprint ||
    parsedFingerprint?.output?.persona_fingerprint ||
    parsedFingerprint ||
    null;
  return {
    id: persona.id,
    name: persona.name,
    avatar_name: persona.avatarName,
    summary: persona.summary,
    worldview: persona.worldview,
    primary_pain: persona.primaryPain,
    deep_fear: persona.deepFear,
    desired_outcome: persona.desiredOutcome,
    awareness_stage: persona.awarenessStage,
    notes: persona.notes,
    fingerprint,
    core_beliefs: safeArray(persona.coreBeliefsJson),
    emotional_hooks: safeArray(persona.emotionalHooksJson),
    secondary_pains: safeArray(persona.secondaryPainsJson),
    objections: safeArray(persona.objectionsJson),
    trust_triggers: safeArray(persona.trustTriggersJson),
    purchase_drivers: safeArray(persona.purchaseDriversJson),
    language_patterns: safeArray(persona.languagePatternsJson),
    customer_voice_quotes: safeArray(persona.customerVoiceQuotesJson),
    exclusions: safeArray(persona.exclusionsJson),
    preferred_channels: persona.preferredChannels,
    proof_required: persona.proofRequired,
    current_frustration: persona.currentFrustration,
    old_solution: persona.oldSolution,
  };
}

function buildCompactProductContext(product: any) {
  if (!product) return null;
  return {
    id: product.id,
    name: firstNonEmpty(product.name, product.title),
    category: product.category,
    description: firstNonEmpty(product.description, product.summary),
    price: firstNonEmpty(product.priceText, product.price),
    role: firstNonEmpty(product.productRole, product.role),
    url: firstNonEmpty(product.pdpUrl, product.url, product.productUrl),
    features: firstNonEmpty(product.ingredientsOrFeatures, product.features, []),
    use_cases: firstNonEmpty(product.useCases, []),
    proof_points: firstNonEmpty(product.proofPoints, []),
    objections: firstNonEmpty(product.objections, []),
    variants: firstNonEmpty(product.variantsJson, product.variants, []),
  };
}

function buildCompactResearchBrief({
  brand,
  persona,
  product,
}: {
  brand: any;
  persona: any;
  product?: any;
}) {
  const compactBrand = buildCompactBrandContext(brand);
  const compactPersona = buildCompactPersonaContext(persona);
  const compactProduct = buildCompactProductContext(product);
  return {
    brand_name: compactBrand?.name || "",
    brand_context: compactBrand,
    known_persona: compactPersona,
    product_or_offer: compactProduct,
    commercial_objective:
      "Generate commercially actionable persona intelligence for messaging, offer strategy, creative development, and conversion improvement.",
    research_goal:
      "Interrogate, enrich, and operationalize the selected persona fingerprint against the selected brand and product/offer context. Identify motivations, objections, trust signals, language patterns, proof needs, channel behavior, and creative/message implications.",
    market_context: {
      category: compactBrand?.category || "",
      competitors: [],
      constraints: compactBrand?.claim_guardrails || [],
      market_notes: compactBrand?.research_notes || "",
    },
  };
}

function getPayloadSizeKb(payload: any) {
  try {
    return Math.round((JSON.stringify(payload).length / 1024) * 10) / 10;
  } catch {
    return null;
  }
}

// ─── Specialist research pairing (Module 4) ─────────────────────────────
// Module 4 runs one Sim call per matched topic/researcher pair. We build
// compact single-topic / single-researcher payloads here, aligned the same
// way the renderer aligns them (by topic id, then position).
function compactResearchTopic(topic: any, fallbackId: any) {
  if (!isPlainObject(topic)) return topic ?? null;
  return {
    id: pickFirst(topic, ["id", "number", "index"]) ?? fallbackId,
    topic: pickFirst(topic, ["topic", "title", "name", "question"]),
    description: pickFirst(topic, ["description", "detail", "summary"]),
    knowledge_gap: pickFirst(topic, ["knowledge_gap", "knowledgeGap", "gap"]),
    commercial_use: pickFirst(topic, ["commercial_use", "commercialUse", "commercial_application"]),
    suggested_sources: firstArray(topic, ["suggested_sources", "suggestedSources", "sources"]) ?? [],
    priority: pickFirst(topic, ["priority", "rank"]),
  };
}

function compactSpecialistResearcher(researcher: any) {
  if (!isPlainObject(researcher)) return researcher ?? null;
  return {
    id: pickFirst(researcher, ["id", "number", "index"]),
    name: pickFirst(researcher, ["name", "researcher_name"]),
    title: pickFirst(researcher, ["title", "role"]),
    assigned_topic_id: pickFirst(researcher, RESEARCHER_TOPIC_ID_KEYS),
    assigned_topic: pickFirst(researcher, RESEARCHER_TOPIC_KEYS),
    background: pickFirst(researcher, RESEARCHER_BACKGROUND_KEYS),
    research_lens: pickFirst(researcher, RESEARCHER_LENS_KEYS),
    source_strategy: firstArray(researcher, RESEARCHER_SRC_KEYS) ?? [],
    commercial_output_focus: pickFirst(researcher, RESEARCHER_COMMERCIAL_KEYS),
    guardrails: firstArray(researcher, RESEARCHER_GUARDRAIL_KEYS) ?? [],
  };
}

interface ResearchPair {
  topicId: any;
  index: number;
  topic: any;
  researcher: any;
}

// Align topics with their matched researchers the same way the renderer does.
function buildResearchPairs(results: Record<string, any>): ResearchPair[] {
  const topicList = firstArray(results.topics, ["research_topics", "topics", "items", "list"]) ?? [];
  return topicList.map((topic, i) => {
    const topicId = pickFirst(topic, ["id", "number", "index"]) ?? i + 1;
    const researcher = findResearcherForTopic(results.researchers, topic, topicId, i);
    return { topicId, index: i, topic, researcher };
  });
}

function buildSpecialistResearchPayload(ctx: BuildContext, pair: ResearchPair): Record<string, any> {
  return {
    brand: ctx.compactBrand,
    persona_fingerprint: ctx.compactPersona,
    product_or_offer: ctx.compactProduct,
    research_brief: ctx.brief,
    lead_strategist: ctx.results.lead,
    research_topic: compactResearchTopic(pair.topic, pair.topicId),
    specialist_researcher: compactSpecialistResearcher(pair.researcher),
  };
}

// ─── Module config ──────────────────────────────────────────────────────
interface BuildContext {
  brief: any;
  compactBrand: any;
  compactPersona: any;
  compactProduct: any;
  results: Record<string, any>;
}

interface ModuleConfig {
  key: string;
  step: number;
  title: string;
  description: string;
  loadingMessage: string;
  requires: string[];
  requiresHint?: string;
  groupHeader?: string;
  helperText?: string;
  buildPayload: (ctx: BuildContext) => Record<string, any>;
}

// Base inputs included with every module: compact brand/persona/product
// contexts plus the compact research brief. Never the raw DB records.
function baseInputs(c: BuildContext): Record<string, any> {
  return {
    brand: c.compactBrand,
    persona_fingerprint: c.compactPersona,
    product_or_offer: c.compactProduct,
    research_brief: c.brief,
  };
}

// Module 4 stores its combined output as { specialist_research: [...], summary }.
// The synthesis workflow expects the per-topic results under a `results` key.
// Normalize without flattening so each insight keeps its topic/researcher.
function buildSpecialistResearchResults(research: any): Record<string, any> {
  const results = firstArray(research, RESEARCH_RESULT_LIST_KEYS) ?? [];
  const summary = isPlainObject(research?.summary) ? research.summary : undefined;
  return summary ? { results, summary } : { results };
}

const WORKFLOW_MODULES: ModuleConfig[] = [
  {
    key: "lead",
    step: 1,
    title: "Generate Research Lead",
    description: "Appoint a lead strategist who frames the research mandate from the selected brand and persona.",
    loadingMessage: "Appointing your research lead and framing the mandate…",
    requires: [],
    buildPayload: (c) => ({ ...baseInputs(c) }),
  },
  {
    key: "topics",
    step: 2,
    title: "Generate Research Topics",
    description: "Creates 10 research topics from persona knowledge gaps, brand awareness questions, trust barriers, buyer motivations, and commercial unknowns.",
    loadingMessage: "Mapping persona knowledge gaps into research priorities…",
    requires: ["lead"],
    buildPayload: (c) => ({
      ...baseInputs(c),
      lead_strategist: c.results.lead,
    }),
  },
  {
    key: "researchers",
    step: 3,
    title: "Generate Specialist Researchers",
    description: "Creates 10 source-aware research agents matched to the generated research topics, knowledge gaps, and suggested evidence sources.",
    loadingMessage: "Assembling source-aware specialist research team…",
    requires: ["lead", "topics"],
    buildPayload: (c) => ({
      ...baseInputs(c),
      lead_strategist: c.results.lead,
      research_topics: c.results.topics?.research_topics ?? c.results.topics,
    }),
  },
  {
    key: "research",
    step: 4,
    title: "Run Specialist Research",
    description: "Each specialist investigates their topic and reports findings.",
    loadingMessage: "Specialists are running their research… this can take a few minutes.",
    requires: ["researchers"],
    // Module 4 does NOT use buildPayload directly. It runs one Sim call per
    // matched topic/researcher pair via buildSpecialistResearchPayload (see
    // runModule). This single-topic/single-researcher payload is the fallback.
    buildPayload: (c) => ({
      ...baseInputs(c),
      lead_strategist: c.results.lead,
    }),
  },
  {
    key: "synthesis",
    step: 5,
    title: "Synthesize Persona Intelligence",
    description: "Synthesizes specialist research into a structured persona intelligence document.",
    loadingMessage: "Synthesizing specialist findings into persona intelligence…",
    requires: ["lead", "topics", "researchers", "research"],
    buildPayload: (c) => ({
      ...baseInputs(c),
      lead_strategist: c.results.lead,
      research_topics: c.results.topics?.research_topics ?? c.results.topics ?? [],
      specialist_researchers:
        firstArray(c.results.researchers, RESEARCHER_LIST_KEYS) ?? [],
      specialist_research_results: buildSpecialistResearchResults(c.results.research),
    }),
  },
  {
    key: "focusGroupModerator",
    step: 6,
    title: "Generate Focus Group Head",
    description: "Appoint the expert moderator who will run the persona focus group — their objective, style, testing priorities, and guardrails.",
    loadingMessage: "Designing your focus group moderator…",
    requires: ["lead", "synthesis"],
    requiresHint: "Requires selected brand, selected persona, research lead, and Persona Intelligence Document.",
    groupHeader: "Persona Focus Group Setup",
    buildPayload: (c) => ({
      ...baseInputs(c),
      lead_strategist: c.results.lead,
      persona_intelligence: c.results.synthesis,
      persona_facets: c.results.synthesis?.audience_profiles || [],
      messaging_implications: c.results.synthesis?.messaging_implications || null,
      recommended_next_steps: c.results.synthesis?.recommended_next_steps || [],
    }),
  },
  {
    key: "focusGroupQuestions",
    step: 7,
    title: "Generate Focus Group Questions",
    description: "Create commercial focus group questions that test persona reactions, objections, proof needs, and messaging themes.",
    loadingMessage: "Writing focus group questions…",
    requires: ["lead", "synthesis", "focusGroupModerator"],
    requiresHint: "Requires Persona Intelligence Document and a generated Focus Group Head.",
    buildPayload: (c) => ({
      ...baseInputs(c),
      lead_strategist: c.results.lead,
      persona_intelligence: c.results.synthesis,
      persona_facets: c.results.synthesis?.audience_profiles || [],
      messaging_implications: c.results.synthesis?.messaging_implications || null,
      recommended_next_steps: c.results.synthesis?.recommended_next_steps || [],
      moderator: c.results.focusGroupModerator?.moderator || c.results.focusGroupModerator,
    }),
  },
  {
    key: "focusGroupTest",
    step: 8,
    title: "Run Persona Focus Group Test",
    description: "Simulates persona-facet participant reactions to the brand, offer, proof, messaging, and conversion path.",
    loadingMessage: "Simulating persona-facet focus group reactions…",
    requires: ["synthesis", "focusGroupModerator", "focusGroupQuestions"],
    requiresHint: "Requires Persona Intelligence Document, Focus Group Head, and Focus Group Questions.",
    helperText: "This is a simulated qualitative test. It does not replace real customer research, but it helps stress-test the persona strategy before creative or offer development.",
    buildPayload: (c) => ({
      ...baseInputs(c),
      lead_strategist: c.results.lead,
      persona_intelligence: c.results.synthesis,
      persona_facets: c.results.synthesis?.audience_profiles || [],
      messaging_implications: c.results.synthesis?.messaging_implications || null,
      recommended_next_steps: c.results.synthesis?.recommended_next_steps || [],
      moderator: c.results.focusGroupModerator?.moderator || c.results.focusGroupModerator,
      focus_group_questions: c.results.focusGroupQuestions?.focus_group_questions || [],
    }),
  },
  {
    key: "finalPersonaBrief",
    step: 9,
    title: "Generate Final Persona Brief",
    description: "Creates the final commercially actionable persona brief from research, synthesis, and focus group testing.",
    loadingMessage: "Generating final persona brief…",
    requires: ["synthesis", "focusGroupModerator", "focusGroupQuestions", "focusGroupTest"],
    requiresHint: "Requires completed Persona Intelligence, Focus Group Head, Focus Group Questions, and Focus Group Test.",
    helperText: "This final brief summarizes what the brand should know, say, show, test, and build for this persona.",
    buildPayload: (c) => ({
      ...baseInputs(c),
      lead_strategist: c.results.lead,
      research_topics: c.results.topics?.research_topics ?? c.results.topics ?? [],
      specialist_researchers: firstArray(c.results.researchers, RESEARCHER_LIST_KEYS) ?? [],
      specialist_research_results: buildSpecialistResearchResults(c.results.research),
      persona_intelligence: c.results.synthesis,
      persona_facets: c.results.synthesis?.audience_profiles || [],
      focus_group_moderator: c.results.focusGroupModerator?.moderator || c.results.focusGroupModerator,
      focus_group_questions: c.results.focusGroupQuestions?.focus_group_questions || [],
      focus_group_test: c.results.focusGroupTest || null,
    }),
  },
];

const MODULE_TITLE: Record<string, string> = Object.fromEntries(
  WORKFLOW_MODULES.map((m) => [m.key, m.title]),
);

// Modules that transitively depend on a given module (via `requires`).
// (Re)generating an upstream module invalidates these downstream results so
// stale output from a prior run never re-attaches to freshly generated inputs
// (e.g. old specialist researchers showing up under newly generated topics).
function downstreamModuleKeys(moduleKey: string): string[] {
  const dependents = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const m of WORKFLOW_MODULES) {
      if (dependents.has(m.key)) continue;
      if (m.requires.some((r) => r === moduleKey || dependents.has(r))) {
        dependents.add(m.key);
        changed = true;
      }
    }
  }
  return Array.from(dependents);
}

// "researchers" and "research" are no longer their own timeline steps — they
// are generated/run from within the Research Topics step and their output is
// shown inside each topic card (assigned researcher + research findings). We
// keep them in WORKFLOW_MODULES (for run/payload/requires wiring) but exclude
// them from the visible timeline.
const TIMELINE_MODULES: ModuleConfig[] = WORKFLOW_MODULES.filter(
  (m) => m.key !== "researchers" && m.key !== "research",
);

// ─── Metadata stripping ─────────────────────────────────────────────────
// Provider/usage metadata is noise for the clean view; we keep it only in
// rawResults. The untouched response is preserved for downstream modules.
const META_KEYS = new Set([
  "model", "tokens", "usage", "toolCalls", "tool_calls", "providerTiming",
  "provider_timing", "cost", "latency", "promptTokens", "completionTokens",
  "totalTokens", "finishReason", "finish_reason",
  // Large/operational Sim fields that can appear nested (Workflow 6 in
  // particular). Stripping these deeply ensures providerTiming.assistantContent
  // is never kept in clean results.
  "timeSegments", "time_segments", "assistantContent", "assistant_content",
  "pricing", "executionId", "execution_id",
]);

function stripMeta(value: any): any {
  if (Array.isArray(value)) return value.map(stripMeta);
  if (value && typeof value === "object") {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      if (META_KEYS.has(k)) continue;
      out[k] = stripMeta(v);
    }
    return out;
  }
  return value;
}

// ─── Workflow 6 (Persona Intelligence) Sim-metadata stripping ────────────
// The Workflow 6 Sim response is large and carries operational metadata that
// is useless downstream and dangerous to store — especially
// providerTiming.timeSegments[].assistantContent, which duplicates the entire
// model answer. Serializing or rendering that can crash the tab or blow the
// localStorage quota. We strip these keys (deeply, so nested copies are also
// removed) the moment Workflow 6 returns, before anything is stored or
// rendered.
const SIM_META_KEYS = new Set([
  "model",
  "tokens",
  "toolCalls",
  "providerTiming",
  "cost",
  "pricing",
  "metadata",
  "executionId",
  "success",
]);

function stripSimMetadata(raw: any): any {
  if (Array.isArray(raw)) return raw.map(stripSimMetadata);
  if (raw && typeof raw === "object") {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (SIM_META_KEYS.has(k)) continue;
      out[k] = stripSimMetadata(v);
    }
    return out;
  }
  return raw;
}

// ─── Raw debug summary ──────────────────────────────────────────────────
// Workflow 6 (synthesis) responses can be very large — the full Sim envelope
// may include providerTiming.timeSegments[].assistantContent, tokens, cost,
// etc. We never store or persist that. Instead we keep only a tiny debug
// summary that is safe to render in the Raw JSON toggle and to write to
// localStorage.
function buildRawDebugSummary(raw: any): any {
  if (!raw || typeof raw !== "object") return raw;
  const segment = raw?.providerTiming?.timeSegments?.[0];
  // Idempotent: handles both a full Sim envelope and an already-summarized
  // object (the server may pre-trim synthesis responses).
  return {
    model: raw?.model,
    tokens: raw?.tokens,
    cost: raw?.cost,
    toolCalls: raw?.toolCalls,
    providerDuration: raw?.providerTiming?.duration ?? raw?.providerDuration,
    provider: segment?.provider ?? raw?.provider,
    finishReason: segment?.finishReason ?? raw?.finishReason,
  };
}

// Reduce any raw value (single envelope or an array of envelopes, as the
// research module stores) to lightweight debug summaries.
function summarizeRaw(raw: any): any {
  if (Array.isArray(raw)) return raw.map(summarizeRaw);
  return buildRawDebugSummary(raw);
}

// Build a localStorage-safe copy of the state. We never persist the full raw
// Sim responses (they can be multiple MB and crash/slow the tab on
// serialization); only lightweight debug summaries are persisted.
function buildPersistableState(state: LabState): LabState {
  const rawResults: Record<string, any> = {};
  for (const [k, v] of Object.entries(state.rawResults ?? {})) {
    rawResults[k] = summarizeRaw(v);
  }
  return { ...state, rawResults };
}

// ─── Background module run + polling ────────────────────────────────────
// Module workflows (especially synthesis / Workflow 6) can run for several
// minutes. Holding one HTTP request open that long makes the Replit proxy
// time out and return a 502 "couldn't reach this app" page. Instead the
// server kicks the run off as a background job and returns a jobId; we poll a
// status endpoint until it completes. Returns { result, raw }.
async function runModuleJob(
  brandId: string,
  body: { moduleKey: string; payload: any },
  opts: { pollIntervalMs?: number; maxWaitMs?: number } = {},
): Promise<{ result: any; raw: any }> {
  const pollIntervalMs = opts.pollIntervalMs ?? 4_000;
  const maxWaitMs = opts.maxWaitMs ?? 20 * 60_000;

  const startRes = await apiRequest("POST", `/api/brands/${brandId}/persona-research-lab/run`, body);
  const start = await startRes.json();

  // Backward-compatible: if the server ever returns a synchronous result
  // (status 200 with a result), use it directly.
  if (start && start.status !== "pending" && (start.result !== undefined || start.raw !== undefined)) {
    return { result: start.result, raw: start.raw };
  }
  const jobId = start?.jobId;
  if (!jobId) {
    throw new Error(start?.message || "Failed to start module run.");
  }

  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, pollIntervalMs));
    const pollRes = await apiRequest(
      "GET",
      `/api/brands/${brandId}/persona-research-lab/run/${jobId}`,
    );
    const poll = await pollRes.json();
    if (poll?.status === "completed") {
      return { result: poll.result, raw: poll.raw };
    }
    if (poll?.status === "failed") {
      throw new Error(poll?.message || "Module run failed.");
    }
    // status "pending" → keep polling.
  }
  throw new Error("Module run timed out. The workflow may still finish in Sim — try again shortly.");
}

function humanizeKey(k: string): string {
  return k
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function downloadJson(filename: string, data: any) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Generic clean JSON renderer (fallback) ─────────────────────────────
function isPlainObject(v: any): boolean {
  return v && typeof v === "object" && !Array.isArray(v);
}

function ScalarValue({ value }: { value: any }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-xs text-muted-foreground italic">—</span>;
  }
  if (typeof value === "boolean") {
    return <span className="text-sm">{value ? "Yes" : "No"}</span>;
  }
  return <span className="text-sm whitespace-pre-wrap leading-snug">{String(value)}</span>;
}

function CleanValue({ value, depth = 0 }: { value: any; depth?: number }) {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-xs text-muted-foreground italic">None</span>;
    }
    const allScalar = value.every((x) => !isPlainObject(x) && !Array.isArray(x));
    if (allScalar) {
      return (
        <ul className="space-y-1">
          {value.map((item, i) => (
            <li key={i} className="text-sm leading-snug flex gap-2">
              <span className="text-primary mt-1.5 flex-shrink-0 text-[8px]">●</span>
              <ScalarValue value={item} />
            </li>
          ))}
        </ul>
      );
    }
    return (
      <div className="space-y-2">
        {value.map((item, i) => (
          <div key={i} className="rounded-md border bg-muted/30 p-2.5">
            <CleanValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) {
    return <CleanObject obj={value} depth={depth + 1} />;
  }

  return <ScalarValue value={value} />;
}

function CleanObject({ obj, depth = 0 }: { obj: Record<string, any>; depth?: number }) {
  const entries = Object.entries(obj);
  if (entries.length === 0) {
    return <p className="text-xs text-muted-foreground italic">Nothing returned.</p>;
  }
  return (
    <div className="space-y-3">
      {entries.map(([k, v]) => {
        const complex = isPlainObject(v) || (Array.isArray(v) && v.some((x) => isPlainObject(x) || Array.isArray(x)));
        return (
          <div key={k} className={complex ? "space-y-1.5" : "flex flex-col gap-1"}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {humanizeKey(k)}
            </p>
            <div className={complex ? "pl-0.5" : ""}>
              <CleanValue value={v} depth={depth} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Module-specific renderers ──────────────────────────────────────────
// Each module gets a tailored card layout that surfaces the most useful
// fields, then gracefully renders any remaining keys with the generic
// renderer so nothing returned by the workflow is ever hidden.
function pickFirst(obj: any, keys: string[]): any {
  if (!isPlainObject(obj)) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

function firstArray(obj: any, keys: string[]): any[] | undefined {
  if (Array.isArray(obj)) return obj;
  if (!isPlainObject(obj)) return undefined;
  for (const k of keys) {
    if (Array.isArray(obj[k]) && obj[k].length) return obj[k];
  }
  return undefined;
}

function Section({ icon: Icon, label, children }: {
  icon?: any;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

// Render any object keys not already consumed by a tailored renderer.
function RestObject({ obj, omit }: { obj: any; omit: string[] }) {
  if (!isPlainObject(obj)) return null;
  const omitSet = new Set(omit);
  const rest: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (omitSet.has(k)) continue;
    if (v === null || v === undefined || v === "") continue;
    rest[k] = v;
  }
  if (Object.keys(rest).length === 0) return null;
  return <CleanObject obj={rest} />;
}

function FallbackOutput({ data }: { data: any }) {
  if (data === null || data === undefined) {
    return <p className="text-xs text-muted-foreground italic">No output yet.</p>;
  }
  if (typeof data === "string") {
    return <p className="text-sm whitespace-pre-wrap leading-snug">{data}</p>;
  }
  if (Array.isArray(data)) return <CleanValue value={data} />;
  if (isPlainObject(data)) return <CleanObject obj={data} />;
  return <ScalarValue value={data} />;
}

function LeadStrategistRenderer({ data }: { data: any }) {
  if (!isPlainObject(data)) return <FallbackOutput data={data} />;
  const name = pickFirst(data, ["name", "lead", "lead_name", "strategist", "title"]);
  const role = pickFirst(data, ["role", "title", "specialty"]);
  const mandate = pickFirst(data, ["mandate", "research_mandate", "framing", "summary", "overview"]);
  const consumed = ["name", "lead", "lead_name", "strategist", "title", "role", "specialty", "mandate", "research_mandate", "framing", "summary", "overview"];
  return (
    <div className="space-y-3">
      {(name || role) && (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0">
            <Compass className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            {name && <p className="text-sm font-semibold leading-tight">{String(name)}</p>}
            {role && <p className="text-xs text-muted-foreground">{String(role)}</p>}
          </div>
        </div>
      )}
      {mandate && (
        <Section icon={Target} label="Research Mandate">
          <p className="text-sm whitespace-pre-wrap leading-snug">{String(mandate)}</p>
        </Section>
      )}
      <RestObject obj={data} omit={consumed} />
    </div>
  );
}

function priorityBadgeClass(priority: string): string {
  const p = priority.toLowerCase();
  if (p.includes("high")) return "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30";
  if (p.includes("med")) return "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30";
  if (p.includes("low")) return "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30";
  return "bg-primary/10 text-primary border-primary/30";
}

// ─── Assigned-researcher linkage (Step 3 shown inside Step 2 topics) ─────
const RESEARCHER_LIST_KEYS = ["personas", "researchers", "specialists", "team", "assignments", "items"];
const RESEARCHER_NAME_KEYS = ["name", "researcher", "specialist"];
const RESEARCHER_TITLE_KEYS = ["title", "role", "specialty", "expertise", "discipline"];
const RESEARCHER_TOPIC_ID_KEYS = ["assigned_topic_id", "assignedTopicId", "topic_id"];
const RESEARCHER_TOPIC_KEYS = ["assigned_topic", "assignedTopic", "topic", "assignment", "focus"];

function findResearcherForTopic(researchersData: any, topic: any, topicNum: any, index: number): any {
  const list = firstArray(researchersData, RESEARCHER_LIST_KEYS);
  if (!list || !list.length) return null;
  const topicTitle = pickFirst(topic, ["topic", "title", "name", "question"]);
  // 1. match by assigned_topic_id === topic id/number
  const byId = list.find((r) => {
    if (!isPlainObject(r)) return false;
    const rid = pickFirst(r, RESEARCHER_TOPIC_ID_KEYS);
    return rid !== undefined && rid !== null && String(rid) === String(topicNum);
  });
  if (byId) return byId;
  // 2. match by assigned_topic text === topic title
  if (topicTitle) {
    const byTopic = list.find((r) =>
      isPlainObject(r) &&
      String(pickFirst(r, RESEARCHER_TOPIC_KEYS) ?? "").trim().toLowerCase() === String(topicTitle).trim().toLowerCase(),
    );
    if (byTopic) return byTopic;
  }
  // 3. fallback by position
  return list[index] ?? null;
}

// Full researcher detail fields — shown when an assigned researcher is expanded.
const RESEARCHER_BACKGROUND_KEYS = ["background", "bio", "experience"];
const RESEARCHER_LENS_KEYS = ["research_lens", "researchLens", "lens", "approach", "methodology"];
const RESEARCHER_SRC_KEYS = ["source_strategy", "sourceStrategy", "sources", "source_focus"];
const RESEARCHER_COMMERCIAL_KEYS = ["commercial_output_focus", "commercialOutputFocus", "commercial_focus", "commercial_use"];
const RESEARCHER_GUARDRAIL_KEYS = ["guardrails", "guard_rails", "quality_control", "constraints"];
const RESEARCHER_ID_KEYS = ["id", "number", "index"];

function ResearcherDetails({ researcher, idx }: { researcher: any; idx: number }) {
  if (!isPlainObject(researcher)) return <ScalarValue value={researcher} />;
  const background = pickFirst(researcher, RESEARCHER_BACKGROUND_KEYS);
  const lens = pickFirst(researcher, RESEARCHER_LENS_KEYS);
  const commercial = pickFirst(researcher, RESEARCHER_COMMERCIAL_KEYS);
  const sourcesRaw = firstArray(researcher, RESEARCHER_SRC_KEYS) ?? [];
  const sources = sourcesRaw
    .map((s) => (typeof s === "string" ? s : String(pickFirst(s, ["name", "label", "source"]) ?? "")))
    .filter(Boolean);
  const guardrailsRaw = firstArray(researcher, RESEARCHER_GUARDRAIL_KEYS) ?? [];
  const guardrails = guardrailsRaw
    .map((g) => (typeof g === "string" ? g : String(pickFirst(g, ["name", "label", "rule"]) ?? "")))
    .filter(Boolean);
  const consumed = [
    ...RESEARCHER_NAME_KEYS, ...RESEARCHER_TITLE_KEYS, ...RESEARCHER_TOPIC_ID_KEYS, ...RESEARCHER_TOPIC_KEYS,
    ...RESEARCHER_BACKGROUND_KEYS, ...RESEARCHER_LENS_KEYS, ...RESEARCHER_SRC_KEYS,
    ...RESEARCHER_COMMERCIAL_KEYS, ...RESEARCHER_GUARDRAIL_KEYS, ...RESEARCHER_ID_KEYS,
  ];
  return (
    <div className="space-y-2.5" data-testid={`researcher-details-${idx}`}>
      {background && (
        <div className="text-xs">
          <span className="font-semibold text-foreground">Background: </span>
          <span className="text-muted-foreground whitespace-pre-wrap">{String(background)}</span>
        </div>
      )}
      {lens && (
        <Section icon={Eye} label="Research Lens">
          <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-snug">{String(lens)}</p>
        </Section>
      )}
      {sources.length > 0 && (
        <Section icon={Database} label="Source Strategy">
          <div className="flex flex-wrap gap-1">
            {sources.map((s, si) => (
              <span
                key={si}
                className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                data-testid={`chip-source-${idx}-${si}`}
              >
                {s}
              </span>
            ))}
          </div>
        </Section>
      )}
      {commercial && (
        <div className="text-xs">
          <span className="font-semibold text-foreground inline-flex items-center gap-1"><Briefcase className="h-3 w-3" /> Commercial output focus: </span>
          <span className="text-muted-foreground whitespace-pre-wrap">{String(commercial)}</span>
        </div>
      )}
      {guardrails.length > 0 && (
        <Section icon={ShieldAlert} label="Guardrails">
          <div className="flex flex-wrap gap-1">
            {guardrails.map((g, gi) => (
              <span
                key={gi}
                className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300"
                data-testid={`chip-guardrail-${idx}-${gi}`}
              >
                <ShieldAlert className="h-2.5 w-2.5" /> {g}
              </span>
            ))}
          </div>
        </Section>
      )}
      <RestObject obj={researcher} omit={consumed} />
    </div>
  );
}

function AssignedResearcherBlock({ researcher, idx, hideStatus }: { researcher: any; idx: number; hideStatus?: boolean }) {
  const [open, setOpen] = useState(false);
  if (!isPlainObject(researcher)) return null;
  const name = pickFirst(researcher, RESEARCHER_NAME_KEYS);
  const title = pickFirst(researcher, RESEARCHER_TITLE_KEYS);
  if (!name && !title) return null;
  return (
    <div className="mt-1 rounded-md border bg-muted/30 p-2.5 space-y-2" data-testid={`assigned-researcher-${idx}`}>
      <div className="flex items-start gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white flex-shrink-0">
          <Users className="h-4 w-4" />
        </div>
        <div className="min-w-0 space-y-1 flex-1">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
            Assigned Researcher
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </div>
          {name && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="flex items-center gap-1 text-sm font-semibold leading-tight text-left hover:text-primary transition-colors"
              data-testid={`button-toggle-researcher-${idx}`}
              aria-expanded={open}
            >
              {String(name)}
              <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
          {title && (
            <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">
              {String(title)}
            </Badge>
          )}
        </div>
      </div>
      {open ? (
        <div className="border-t pt-2 mt-1">
          <ResearcherDetails researcher={researcher} idx={idx} />
        </div>
      ) : !hideStatus ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          Ready for Research
        </div>
      ) : null}
    </div>
  );
}

type ResearchersControl = {
  status: ModuleStatus;
  error?: string;
  running: boolean;
  anyRunning: boolean;
  configured: boolean;
  hasResult: boolean;
  onGenerate: () => void;
};

type ResearchControl = {
  status: ModuleStatus;
  error?: string;
  running: boolean;
  anyRunning: boolean;
  configured: boolean;
  hasResult: boolean;
  canRun: boolean;
  onGenerate: () => void;
};

function ResearchTopicsRenderer({
  data,
  researchers,
  control,
  research,
  researchControl,
  researchRunning,
}: {
  data: any;
  researchers?: any;
  control?: ResearchersControl;
  research?: any;
  researchControl?: ResearchControl;
  researchRunning?: boolean;
}) {
  const topics = firstArray(data, ["research_topics", "topics", "items", "list"]);
  if (!topics) return <FallbackOutput data={data} />;
  const TITLE_KEYS = ["topic", "title", "name", "question"];
  const DESC_KEYS = ["description", "why", "rationale", "reason", "objective"];
  const GAP_KEYS = ["knowledge_gap", "knowledgeGap", "gap"];
  const USE_KEYS = ["commercial_use", "commercialUse", "commercial_value", "use", "application"];
  const SRC_KEYS = ["suggested_sources", "suggestedSources", "sources"];
  const PRIORITY_KEYS = ["priority", "importance", "rank"];
  const ID_KEYS = ["id", "number", "index"];
  // Specialist-research progress (Completed / Total Insights / bar). Computed up
  // here so it can render at the top of the card while research is underway.
  const researchProgress = researchControl
    ? (() => {
        const resultList = firstArray(research, RESEARCH_RESULT_LIST_KEYS) ?? [];
        const hasRun = firstArray(research, RESEARCH_RESULT_LIST_KEYS) != null;
        const completed = resultList.filter(
          (r) => (firstArray(r, INSIGHT_LIST_KEYS)?.length ?? 0) > 0,
        ).length;
        const totalInsights = resultList.reduce(
          (sum, r) => sum + (firstArray(r, INSIGHT_LIST_KEYS)?.length ?? 0),
          0,
        );
        const targetInsights = topics.length * RESEARCH_TARGET_INSIGHTS;
        const progressPct = targetInsights > 0 ? Math.min(100, Math.round((totalInsights / targetInsights) * 100)) : 0;
        return { completed, totalInsights, targetInsights, progressPct, showProgress: hasRun || researchControl.running };
      })()
    : null;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <ListChecks className="h-3.5 w-3.5" /> Research Topics ({topics.length})
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          {/* Specialist researchers are generated once; regeneration is
              intentionally not offered, so the button hides after assignment. */}
          {control && !control.hasResult && (
            <Button
              size="sm"
              onClick={control.onGenerate}
              disabled={control.running || control.anyRunning || !control.configured}
              data-testid="button-generate-researchers"
            >
              {control.running ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</>
              ) : (
                <><Microscope className="h-3.5 w-3.5 mr-1.5" /> Generate Specialist Researchers</>
              )}
            </Button>
          )}
          {/* Once specialist researchers have been assigned (researchControl.canRun),
              surface the "Run Specialist Research" action here in the header so it's
              visible alongside the topics — not buried at the bottom of the card. */}
          {researchControl && researchControl.canRun && (
            <Button
              size="sm"
              onClick={researchControl.onGenerate}
              disabled={researchControl.running || researchControl.anyRunning || !researchControl.configured}
              data-testid="button-run-research"
            >
              {researchControl.running ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Researching…</>
              ) : researchControl.hasResult ? (
                <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-run Specialist Research</>
              ) : (
                <><Microscope className="h-3.5 w-3.5 mr-1.5" /> Run Specialist Research</>
              )}
            </Button>
          )}
        </div>
      </div>
      {researchProgress?.showProgress && (
        <div className="space-y-1.5 rounded-lg border border-dashed bg-muted/20 p-3" data-testid="research-progress">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="text-foreground"><span className="font-semibold text-emerald-600 dark:text-emerald-400">Completed:</span> {researchProgress.completed} / {topics.length}</span>
            <span className="text-foreground"><span className="font-semibold text-primary">Total Insights:</span> {researchProgress.totalInsights} / {researchProgress.targetInsights}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${researchProgress.progressPct}%` }} data-testid="research-progress-bar" />
          </div>
          {researchControl?.running && (
            <p className="text-xs text-muted-foreground">Specialists are running their research… this can take a few minutes.</p>
          )}
        </div>
      )}
      {topics.length !== 10 && (
        <div className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-300" data-testid="warning-topic-count">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          Expected 10 research topics; received {topics.length}.
        </div>
      )}
      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 md:items-start">
        {topics.map((t, i) => {
          if (!isPlainObject(t)) {
            return (
              <div key={i} className="flex gap-2 text-sm" data-testid={`card-topic-${i}`}>
                <span className="text-primary font-semibold">{i + 1}.</span>
                <ScalarValue value={t} />
              </div>
            );
          }
          const num = pickFirst(t, ID_KEYS) ?? i + 1;
          const title = pickFirst(t, TITLE_KEYS);
          const description = pickFirst(t, DESC_KEYS);
          const gap = pickFirst(t, GAP_KEYS);
          const use = pickFirst(t, USE_KEYS);
          const priority = pickFirst(t, PRIORITY_KEYS);
          const sourcesRaw = firstArray(t, SRC_KEYS) ?? [];
          const sources = sourcesRaw.map((s) => (typeof s === "string" ? s : String(pickFirst(s, ["name", "label", "source"]) ?? ""))).filter(Boolean);
          const consumed = [...TITLE_KEYS, ...DESC_KEYS, ...GAP_KEYS, ...USE_KEYS, ...SRC_KEYS, ...PRIORITY_KEYS, ...ID_KEYS];
          return (
            <div key={i} className="rounded-lg border bg-card p-3 space-y-2" data-testid={`card-topic-${i}`}>
              <div className="flex items-start gap-2">
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[11px] font-semibold text-primary flex-shrink-0">
                  {String(num)}
                </span>
                <p className="text-sm font-semibold leading-snug flex-1">{title ? String(title) : `Topic ${i + 1}`}</p>
                {priority && (
                  <Badge variant="outline" className={`text-[10px] capitalize ${priorityBadgeClass(String(priority))}`}>
                    {String(priority)}
                  </Badge>
                )}
              </div>
              {description && (
                <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-snug">{String(description)}</p>
              )}
              {gap && (
                <div className="text-xs">
                  <span className="font-semibold text-foreground">Knowledge gap: </span>
                  <span className="text-muted-foreground whitespace-pre-wrap">{String(gap)}</span>
                </div>
              )}
              {use && (
                <div className="text-xs">
                  <span className="font-semibold text-foreground">Commercial use: </span>
                  <span className="text-muted-foreground whitespace-pre-wrap">{String(use)}</span>
                </div>
              )}
              {sources.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-0.5">
                  {sources.map((s, si) => (
                    <span
                      key={si}
                      className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                      data-testid={`chip-source-${i}-${si}`}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}
              {(() => {
                const researcher = findResearcherForTopic(researchers, t, num, i);
                if (researcher) return <AssignedResearcherBlock researcher={researcher} idx={i} />;
                if (control?.running) return <SpecialistHiringMini idx={i} />;
                return null;
              })()}
              <TopicResearchOutput topic={t} topicNum={num} idx={i} research={research} running={!!researchRunning} />
              <RestObject obj={t} omit={consumed} />
            </div>
          );
        })}
      </div>
      {control && (() => {
        const researcherList = firstArray(researchers, RESEARCHER_LIST_KEYS);
        const researcherCount = researcherList?.length ?? 0;
        const showCountWarning =
          control.hasResult && researcherCount > 0 && researcherCount !== topics.length;
        // The "Generate Specialist Researchers" action now lives in the Research
        // Topics header above, so this block only renders when there's status or
        // feedback to surface (otherwise it would be an empty box).
        const hasFeedback =
          !control.configured ||
          control.running ||
          (control.status === "error" && !!control.error) ||
          showCountWarning;
        if (!hasFeedback) return null;
        return (
          <div className="space-y-2 rounded-lg border border-dashed bg-muted/20 p-3" data-testid="researchers-control">
            {!control.configured && (
              <div className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-300" data-testid="warning-researchers-unconfigured">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                This module's Sim workflow endpoint is not configured yet.
              </div>
            )}
            {control.running && (
              <p className="text-xs text-muted-foreground">Assembling source-aware specialist research team…</p>
            )}
            {control.status === "error" && control.error && (
              <div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive" data-testid="error-researchers">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap">{control.error}</span>
              </div>
            )}
            {showCountWarning && (
              <div className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-300" data-testid="warning-researcher-count">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                Expected {topics.length} specialist researchers; received {researcherCount}.
              </div>
            )}
          </div>
        );
      })()}
      {researchControl && (() => {
        // Progress now renders at the top of the card (see researchProgress
        // block); this block only carries warnings/errors, gated so it never
        // renders an empty box.
        const hasFeedback =
          !researchControl.configured ||
          (researchControl.status === "error" && !!researchControl.error);
        if (!hasFeedback) return null;
        return (
          <div className="space-y-2 rounded-lg border border-dashed bg-muted/20 p-3" data-testid="research-control">
            {!researchControl.configured && (
              <div className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-300" data-testid="warning-research-unconfigured">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                This module's Sim workflow endpoint is not configured yet.
              </div>
            )}
            {researchControl.status === "error" && researchControl.error && (
              <div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive" data-testid="error-research">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap">{researchControl.error}</span>
              </div>
            )}
          </div>
        );
      })()}
      {isPlainObject(data) && <RestObject obj={data} omit={["research_topics", "topics", "items", "list"]} />}
    </div>
  );
}

// Format a raw URL as a clean domain label (businessurope.eu, reddit.com…).
function formatSourceLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

// Resolve a favicon for a source domain via Google's public favicon service.
function faviconUrl(url: string): string | null {
  try {
    const host = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?sz=64&domain=${host}`;
  } catch {
    return null;
  }
}

// Renders a source URL styled as a compact "mini browser" window — traffic-light
// dots, an address bar with the site favicon + domain, and an optional caption.
function SourceBrowserChip({
  url,
  caption,
  testId,
}: {
  url: string;
  caption?: string;
  testId?: string;
}) {
  const [favError, setFavError] = useState(false);
  // Reset the favicon fallback when the URL changes (keys may be reused by index).
  useEffect(() => setFavError(false), [url]);
  const domain = formatSourceLabel(url);
  const fav = faviconUrl(url);
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={url}
      className="group inline-flex max-w-full flex-col overflow-hidden rounded-lg border bg-card shadow-sm transition-all hover:shadow-md hover:border-primary/40"
      data-testid={testId}
    >
      <span className="flex items-center gap-1.5 border-b bg-muted/60 px-2 py-1">
        <span className="flex flex-shrink-0 items-center gap-1" aria-hidden="true">
          <span className="h-2 w-2 rounded-full bg-red-400/80" />
          <span className="h-2 w-2 rounded-full bg-amber-400/80" />
          <span className="h-2 w-2 rounded-full bg-emerald-400/80" />
        </span>
        <span className="ml-0.5 flex min-w-0 flex-1 items-center gap-1.5 rounded-full border bg-background px-2 py-0.5">
          {fav && !favError ? (
            <img
              src={fav}
              alt=""
              loading="lazy"
              className="h-3.5 w-3.5 flex-shrink-0 rounded-sm"
              onError={() => setFavError(true)}
            />
          ) : (
            <Globe className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
          )}
          <span className="truncate text-[11px] font-medium text-foreground">{domain}</span>
          <ExternalLink className="ml-auto h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </span>
      </span>
      {caption && (
        <span className="px-2.5 py-1 text-[10px] text-muted-foreground">{caption}</span>
      )}
    </a>
  );
}

const RESEARCH_RESULT_LIST_KEYS = [
  "specialist_research", "specialistResearch", "research_results", "researchResults",
  "research", "findings", "reports", "results", "items",
];
const RESEARCH_TOPIC_ID_KEYS = ["topic_id", "topicId", "assigned_topic_id", "assignedTopicId", "id", "number", "index"];
const INSIGHT_LIST_KEYS = ["insights", "findings", "evidence", "key_findings", "items"];
const RESEARCH_TARGET_INSIGHTS = 5;

type ResearchItemStatus = "complete" | "partial" | "empty";

function researchSourceList(insight: any): string[] {
  const raw = pickFirst(insight, ["source", "sources", "url", "link", "citation", "citations"]);
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr
    .map((s) => (typeof s === "string" ? s : String(pickFirst(s, ["url", "link", "source", "href"]) ?? "")))
    .filter(Boolean);
}

function ResearchInsightCard({ insight, topicIdx, idx }: { insight: any; topicIdx: number; idx: number }) {
  if (!isPlainObject(insight)) {
    return (
      <div className="rounded-md border bg-card p-2.5 text-xs" data-testid={`insight-${topicIdx}-${idx}`}>
        <ScalarValue value={insight} />
      </div>
    );
  }
  const summary = pickFirst(insight, ["summary", "headline", "title", "insight", "finding"]);
  const details = pickFirst(insight, ["details", "detail", "description", "explanation", "body", "evidence"]);
  const evidenceType = pickFirst(insight, ["evidence_type", "evidenceType", "type"]);
  const confidence = pickFirst(insight, ["confidence", "confidence_level", "certainty"]);
  const personaRelevance = pickFirst(insight, ["persona_relevance", "personaRelevance", "relevance"]);
  const commercialUse = pickFirst(insight, ["commercial_use", "commercialUse", "commercial_value"]);
  const limitations = pickFirst(insight, ["limitations", "limitation", "caveats", "caveat"]);
  const sources = researchSourceList(insight);
  const consumed = [
    "summary", "headline", "title", "insight", "finding",
    "details", "detail", "description", "explanation", "body", "evidence",
    "evidence_type", "evidenceType", "type",
    "confidence", "confidence_level", "certainty",
    "persona_relevance", "personaRelevance", "relevance",
    "commercial_use", "commercialUse", "commercial_value",
    "limitations", "limitation", "caveats", "caveat",
    "source", "sources", "url", "link", "citation", "citations",
  ];
  return (
    <div className="rounded-md border bg-card p-2.5 space-y-1.5" data-testid={`insight-${topicIdx}-${idx}`}>
      {summary && <p className="text-xs font-semibold leading-snug text-foreground">{String(summary)}</p>}
      {details && <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-snug">{String(details)}</p>}
      {(evidenceType || confidence) && (
        <div className="flex items-center gap-1 flex-wrap">
          {evidenceType && <Badge variant="secondary" className="text-[10px] capitalize">{String(evidenceType)}</Badge>}
          {confidence && (
            <Badge variant="outline" className="text-[10px] capitalize border-primary/30 text-primary bg-primary/10">
              {String(confidence)} confidence
            </Badge>
          )}
        </div>
      )}
      {personaRelevance && (
        <p className="text-xs"><span className="font-semibold text-foreground">Persona relevance: </span><span className="text-muted-foreground">{String(personaRelevance)}</span></p>
      )}
      {commercialUse && (
        <p className="text-xs"><span className="font-semibold text-foreground">Commercial use: </span><span className="text-muted-foreground">{String(commercialUse)}</span></p>
      )}
      {sources.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-0.5">
          {sources.map((s, si) =>
            /^https?:\/\//i.test(s) ? (
              <SourceBrowserChip
                key={si}
                url={s}
                caption="Research source"
                testId={`source-${topicIdx}-${idx}-${si}`}
              />
            ) : (
              <span
                key={si}
                className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                data-testid={`source-${topicIdx}-${idx}-${si}`}
              >
                {s}
              </span>
            ),
          )}
        </div>
      )}
      {limitations && (
        <p className="text-[11px] text-muted-foreground italic"><span className="font-semibold not-italic">Limitations: </span>{String(limitations)}</p>
      )}
      <RestObject obj={insight} omit={consumed} />
    </div>
  );
}

function FutureSourceGaps({ gaps, topicIdx }: { gaps: any[]; topicIdx: number }) {
  const [open, setOpen] = useState(false);
  if (!gaps.length) return null;
  return (
    <div className="rounded-md border border-dashed bg-muted/20 p-2" data-testid={`future-gaps-${topicIdx}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        data-testid={`button-future-gaps-${topicIdx}`}
        aria-expanded={open}
      >
        <Compass className="h-3.5 w-3.5" /> Future Source Gaps ({gaps.length})
        <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-1.5 pt-2">
          {gaps.map((g, gi) => {
            if (!isPlainObject(g)) return <div key={gi} className="text-xs"><ScalarValue value={g} /></div>;
            const type = pickFirst(g, ["source_type", "sourceType", "type"]);
            const reason = pickFirst(g, ["reason", "why", "rationale"]);
            const target = pickFirst(g, ["suggested_query_or_collection_target", "suggested_query", "target", "suggestion"]);
            return (
              <div key={gi} className="text-xs space-y-1 rounded-md border bg-card p-2">
                {type && <Badge variant="outline" className="text-[10px]">{String(type)}</Badge>}
                {reason && <p className="text-muted-foreground">{String(reason)}</p>}
                {target && <p className="text-muted-foreground"><span className="font-semibold text-foreground">Target: </span>{String(target)}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Per-topic research findings rendered directly under a topic's assigned
// researcher (inside the Research Topics step). Returns null until research has
// run or is running for this topic, so unrun topics stay clean.
function findResearchResultForTopic(research: any, topicNum: any, idx: number): any {
  const resultList = firstArray(research, RESEARCH_RESULT_LIST_KEYS) ?? [];
  if (!resultList.length) return null;
  const byId = resultList.find((r) => {
    if (!isPlainObject(r)) return false;
    const rid = pickFirst(r, RESEARCH_TOPIC_ID_KEYS);
    return rid !== undefined && rid !== null && String(rid) === String(topicNum);
  });
  return byId ?? resultList[idx] ?? null;
}

function TopicResearchOutput({
  topic,
  topicNum,
  idx,
  research,
  running,
}: {
  topic: any;
  topicNum: any;
  idx: number;
  research?: any;
  running?: boolean;
}) {
  const hasRun = firstArray(research, RESEARCH_RESULT_LIST_KEYS) != null;
  const result = findResearchResultForTopic(research, topicNum, idx);
  const insights = (result ? firstArray(result, INSIGHT_LIST_KEYS) : null) ?? [];
  const insightCount = insights.length;
  const gaps = (result ? firstArray(result, ["future_source_gaps", "futureSourceGaps", "source_gaps", "gaps"]) : null) ?? [];

  // Nothing to show until a research run exists (or is in flight).
  if (!hasRun && !running) return null;

  const status: ResearchItemStatus =
    insightCount >= RESEARCH_TARGET_INSIGHTS ? "complete" : insightCount > 0 ? "partial" : "empty";

  return (
    <div className="border-t pt-2 mt-1 space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium" data-testid={`research-status-${idx}`}>
        {status === "complete" ? (
          <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /><span className="text-emerald-600 dark:text-emerald-400">Research Complete ({insightCount}/{Math.max(RESEARCH_TARGET_INSIGHTS, insightCount)})</span></>
        ) : status === "partial" ? (
          <><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /><span className="text-amber-600 dark:text-amber-400">Partial Research Complete ({insightCount}/{RESEARCH_TARGET_INSIGHTS})</span></>
        ) : running ? (
          <><Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /><span className="text-primary">Researching…</span></>
        ) : (
          <><AlertTriangle className="h-3.5 w-3.5 text-destructive" /><span className="text-destructive">No insights returned</span></>
        )}
      </div>

      {insights.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Lightbulb className="h-3.5 w-3.5" /> Insights
          </div>
          {insights.map((ins, ii) => (
            <ResearchInsightCard key={ii} insight={ins} topicIdx={idx} idx={ii} />
          ))}
        </div>
      ) : running ? (
        <p className="text-xs text-muted-foreground">This researcher is collecting evidence and extracting persona-relevant insights.</p>
      ) : null}

      <FutureSourceGaps gaps={gaps} topicIdx={idx} />
    </div>
  );
}

function SpecialistResearchRenderer({ data, topics, researchers, running }: { data: any; topics?: any; researchers?: any; running?: boolean }) {
  const topicList = firstArray(topics, ["research_topics", "topics", "items", "list"]) ?? [];
  const resultList = firstArray(data, RESEARCH_RESULT_LIST_KEYS) ?? [];

  // Without the upstream topics we cannot align cards — fall back gracefully.
  if (!topicList.length) {
    if (!resultList.length) return <FallbackOutput data={data} />;
    return (
      <div className="space-y-2.5">
        {resultList.map((r, i) => (
          <div key={i} className="rounded-lg border bg-card p-3">
            <ResearcherDetails researcher={r} idx={i} />
          </div>
        ))}
      </div>
    );
  }

  const findResultForTopic = (topicId: any, index: number): any => {
    const byId = resultList.find((r) => {
      if (!isPlainObject(r)) return false;
      const rid = pickFirst(r, RESEARCH_TOPIC_ID_KEYS);
      return rid !== undefined && rid !== null && String(rid) === String(topicId);
    });
    if (byId) return byId;
    return resultList[index] ?? null;
  };

  const cards = topicList.map((topic, i) => {
    const topicNum = pickFirst(topic, ["id", "number", "index"]) ?? i + 1;
    const researcher = findResearcherForTopic(researchers, topic, topicNum, i);
    const result = findResultForTopic(topicNum, i);
    const insights = (result ? firstArray(result, INSIGHT_LIST_KEYS) : null) ?? [];
    const insightCount = insights.length;
    const status: ResearchItemStatus =
      insightCount >= RESEARCH_TARGET_INSIGHTS ? "complete" : insightCount > 0 ? "partial" : "empty";
    return { topic, topicNum, researcher, result, insights, insightCount, status, i };
  });

  const totalInsights = cards.reduce((sum, c) => sum + c.insightCount, 0);
  const targetInsights = cards.length * RESEARCH_TARGET_INSIGHTS;
  const completedCount = cards.filter((c) => c.status !== "empty").length;
  const failedCount = cards.filter((c) => c.status === "empty").length;
  const progressPct = targetInsights > 0 ? Math.min(100, Math.round((totalInsights / targetInsights) * 100)) : 0;

  return (
    <div className="space-y-3">
      {/* Module-level progress */}
      <div className="space-y-2 rounded-lg border bg-muted/20 p-3" data-testid="research-progress">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <Microscope className="h-3.5 w-3.5" /> Specialist Research Progress
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span className="text-foreground"><span className="font-semibold text-emerald-600 dark:text-emerald-400">Completed:</span> {completedCount} / {cards.length}</span>
          <span className="text-foreground"><span className="font-semibold text-destructive">Failed:</span> {failedCount}</span>
          <span className="text-foreground"><span className="font-semibold text-primary">Total Insights:</span> {totalInsights} / {targetInsights}</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progressPct}%` }} data-testid="research-progress-bar" />
        </div>
      </div>

      {/* One card per topic / researcher pair */}
      <div className="space-y-2.5">
        {cards.map((c) => {
          const title = pickFirst(c.topic, ["topic", "title", "name", "question"]);
          const description = pickFirst(c.topic, ["description", "why", "rationale", "reason", "objective"]);
          const priority = pickFirst(c.topic, ["priority", "importance", "rank"]);
          const sourcesRaw = firstArray(c.topic, ["suggested_sources", "suggestedSources", "sources"]) ?? [];
          const suggestedSources = sourcesRaw
            .map((s) => (typeof s === "string" ? s : String(pickFirst(s, ["name", "label", "source"]) ?? "")))
            .filter(Boolean);
          const gaps = (c.result ? firstArray(c.result, ["future_source_gaps", "futureSourceGaps", "source_gaps", "gaps"]) : null) ?? [];
          return (
            <div key={c.i} className="rounded-lg border bg-card p-3 space-y-2.5" data-testid={`card-research-${c.i}`}>
              {/* Topic header */}
              <div className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[11px] font-semibold text-primary flex-shrink-0">
                    {String(c.topicNum)}
                  </span>
                  <p className="text-sm font-semibold leading-snug flex-1">{title ? String(title) : `Topic ${c.i + 1}`}</p>
                  {priority && (
                    <Badge variant="outline" className={`text-[10px] capitalize ${priorityBadgeClass(String(priority))}`}>
                      {String(priority)}
                    </Badge>
                  )}
                </div>
                {description && <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-snug">{String(description)}</p>}
                {suggestedSources.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {suggestedSources.map((s, si) => (
                      <span key={si} className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground" data-testid={`chip-suggested-${c.i}-${si}`}>
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Assigned researcher */}
              {c.researcher ? (
                <AssignedResearcherBlock researcher={c.researcher} idx={c.i} hideStatus />
              ) : (
                <p className="text-xs text-muted-foreground italic">No matched researcher found for this topic.</p>
              )}

              {/* Research status */}
              <div className="flex items-center gap-1.5 text-xs font-medium" data-testid={`research-status-${c.i}`}>
                {c.status === "complete" ? (
                  <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /><span className="text-emerald-600 dark:text-emerald-400">Research Complete ({c.insightCount}/{Math.max(RESEARCH_TARGET_INSIGHTS, c.insightCount)})</span></>
                ) : c.status === "partial" ? (
                  <><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /><span className="text-amber-600 dark:text-amber-400">Partial Research Complete ({c.insightCount}/{RESEARCH_TARGET_INSIGHTS})</span></>
                ) : running ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /><span className="text-primary">Researching…</span></>
                ) : (
                  <><AlertTriangle className="h-3.5 w-3.5 text-destructive" /><span className="text-destructive">No insights returned</span></>
                )}
              </div>

              {/* Insights */}
              {c.insights.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Lightbulb className="h-3.5 w-3.5" /> Insights
                  </div>
                  {c.insights.map((ins, ii) => (
                    <ResearchInsightCard key={ii} insight={ins} topicIdx={c.i} idx={ii} />
                  ))}
                </div>
              ) : running ? (
                <p className="text-xs text-muted-foreground">This researcher is collecting evidence and extracting persona-relevant insights.</p>
              ) : null}

              <FutureSourceGaps gaps={gaps} topicIdx={c.i} />
            </div>
          );
        })}
      </div>
      {isPlainObject(data) && <RestObject obj={data} omit={[...RESEARCH_RESULT_LIST_KEYS, "summary"]} />}
    </div>
  );
}

// ─── Persona Intelligence Document (Module 5 / Workflow 6) ───────────────
// Renders the synthesized persona intelligence as a structured document with
// 13 ordered sections. Resilient to missing or alternately-named fields.

function piSourceList(item: any): string[] {
  const raw = pickFirst(item, [
    "supporting_sources", "supportingSources", "sources", "source",
    "evidence", "citations", "citation", "links", "url",
  ]);
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr
    .map((s) => (typeof s === "string" ? s : String(pickFirst(s, ["url", "link", "source", "href", "name", "label"]) ?? "")))
    .filter(Boolean);
}

function PiSourceLinks({ sources, idPrefix }: { sources: string[]; idPrefix: string }) {
  if (!sources.length) return null;
  return (
    <div className="flex flex-wrap gap-2 pt-0.5">
      {sources.map((s, si) => {
        const isUrl = /^https?:\/\//i.test(s);
        return isUrl ? (
          <SourceBrowserChip
            key={si}
            url={s}
            testId={`pi-source-${idPrefix}-${si}`}
          />
        ) : (
          <span
            key={si}
            className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
            data-testid={`pi-source-${idPrefix}-${si}`}
          >
            {s}
          </span>
        );
      })}
    </div>
  );
}

function PiConfidenceBadge({ value }: { value: any }) {
  if (!value) return null;
  return (
    <Badge variant="outline" className="text-[10px] capitalize border-primary/30 text-primary bg-primary/10">
      {String(value)} confidence
    </Badge>
  );
}

// Generic card for an array of insight-like objects. `fields` maps a label to
// the candidate keys; the first present value is rendered. Confidence and
// supporting sources are handled separately.
function PiInsightCards({
  items,
  fields,
  idPrefix,
}: {
  items: any[];
  fields: { label: string; keys: string[]; emphasis?: boolean }[];
  idPrefix: string;
}) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => {
        if (!isPlainObject(item)) {
          return (
            <div key={i} className="rounded-md border bg-card p-2.5 text-sm" data-testid={`pi-card-${idPrefix}-${i}`}>
              <ScalarValue value={item} />
            </div>
          );
        }
        const confidence = pickFirst(item, ["confidence", "confidence_level", "certainty"]);
        const sources = piSourceList(item);
        const consumed = [
          ...fields.flatMap((f) => f.keys),
          "confidence", "confidence_level", "certainty",
          "supporting_sources", "supportingSources", "sources", "source",
          "evidence", "citations", "citation", "links", "url",
        ];
        return (
          <div key={i} className="rounded-md border bg-card p-2.5 space-y-1.5" data-testid={`pi-card-${idPrefix}-${i}`}>
            {fields.map((f, fi) => {
              const val = pickFirst(item, f.keys);
              if (val == null || val === "") return null;
              return f.emphasis ? (
                <p key={fi} className="text-sm font-semibold leading-snug text-foreground">{String(val)}</p>
              ) : (
                <p key={fi} className="text-xs">
                  <span className="font-semibold text-foreground">{f.label}: </span>
                  <span className="text-muted-foreground whitespace-pre-wrap">{String(val)}</span>
                </p>
              );
            })}
            {confidence && <div><PiConfidenceBadge value={confidence} /></div>}
            <PiSourceLinks sources={sources} idPrefix={`${idPrefix}-${i}`} />
            <RestObject obj={item} omit={consumed} />
          </div>
        );
      })}
    </div>
  );
}

// Renders a list whose items can be strings or simple objects, as bullet cards.
function PiBulletCards({ items, idPrefix }: { items: any[]; idPrefix: string }) {
  return (
    <div className="space-y-1.5">
      {items.map((item, i) =>
        typeof item === "string" ? (
          <div key={i} className="flex gap-2 text-sm" data-testid={`pi-bullet-${idPrefix}-${i}`}>
            <span className="text-primary mt-1.5 h-1 w-1 rounded-full bg-primary flex-shrink-0" />
            <span className="text-muted-foreground leading-snug">{item}</span>
          </div>
        ) : (
          <div key={i} className="rounded-md border bg-card p-2.5 text-sm" data-testid={`pi-bullet-${idPrefix}-${i}`}>
            <CleanValue value={item} />
          </div>
        ),
      )}
    </div>
  );
}

// Renders a labeled subsection containing a string list or bullet cards.
function PiSubList({ label, items, idPrefix }: { label: string; items: any[] | undefined; idPrefix: string }) {
  if (!items || !items.length) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <PiBulletCards items={items} idPrefix={idPrefix} />
    </div>
  );
}

// ─── Workflow 6 (Persona Intelligence) document rendering ───────────────
// The Persona Intelligence Document is rendered in a FIXED section order —
// never in raw JSON key order — so appendix_sources can never appear first.
const PERSONA_INTELLIGENCE_SECTION_ORDER = [
  "executive_summary",
  "recommended_next_steps",
  "consolidated_findings",
  "audience_profiles",
  "messaging_implications",
  "appendix_sources",
] as const;

const KNOWN_PERSONA_INTELLIGENCE_KEYS = new Set<string>([
  "executive_summary",
  "recommended_next_steps",
  "consolidated_findings",
  "audience_profiles",
  "messaging_implications",
  "appendix_sources",
]);

// Operational Sim metadata that must never appear in the document view.
const PERSONA_INTELLIGENCE_METADATA_KEYS = new Set<string>([
  "cost", "model", "tokens", "toolCalls", "providerTiming",
  "pricing", "metadata", "executionId", "success", "assistantContent",
]);

const PERSONA_FACETS_HELP_TEXT =
  "Persona facets are not new audiences. They are commercially important decision modes within the selected persona — for example, safety-focused, referral-driven, price/risk-sensitive, or proof-seeking versions of the same buyer.";

function toStringList(value: any): string[] {
  if (value === null || value === undefined) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr
    .map((v) => (typeof v === "string" ? v : String(pickFirst(v, ["text", "value", "label", "name", "url", "link"]) ?? "")))
    .map((s) => s.trim())
    .filter(Boolean);
}

// Appendix: Research Sources — collapsed by default, with "Show all" expansion
// when the list is long.
function PersonaIntelligenceAppendix({ sources }: { sources: string[] }) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const LIMIT = 12;
  const visible = showAll ? sources : sources.slice(0, LIMIT);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          data-testid="pi-appendix-toggle"
        >
          <Database className="h-3.5 w-3.5" />
          Appendix: Research Sources ({sources.length})
          <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="rounded-lg border bg-card p-3 space-y-2" data-testid="pi-appendix-sources">
          <div className="flex flex-wrap gap-2">
            {visible.map((s, si) => {
              const isUrl = /^https?:\/\//i.test(s);
              return isUrl ? (
                <SourceBrowserChip
                  key={si}
                  url={s}
                  testId={`pi-appendix-source-${si}`}
                />
              ) : (
                <span
                  key={si}
                  className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                  data-testid={`pi-appendix-source-${si}`}
                >
                  {s}
                </span>
              );
            })}
          </div>
          {sources.length > LIMIT && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => setShowAll((v) => !v)}
              data-testid="button-pi-appendix-show-all"
            >
              {showAll ? "Show fewer sources" : `Show all sources (${sources.length})`}
            </Button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function PersonaIntelligenceRenderer({ data }: { data: any }) {
  if (!isPlainObject(data)) return <FallbackOutput data={data} />;

  // Defensive: ensure no operational Sim metadata leaks into the document view.
  const doc: Record<string, any> = {};
  for (const [k, v] of Object.entries(data)) {
    if (PERSONA_INTELLIGENCE_METADATA_KEYS.has(k)) continue;
    doc[k] = v;
  }

  const execSummary = pickFirst(doc, ["executive_summary", "executiveSummary"]);
  const nextSteps = toStringList(pickFirst(doc, ["recommended_next_steps", "recommendedNextSteps"]));
  const findings = firstArray(doc, ["consolidated_findings", "consolidatedFindings"]) ?? [];
  const facets = firstArray(doc, ["audience_profiles", "audienceProfiles"]) ?? [];
  const messaging = pickFirst(doc, ["messaging_implications", "messagingImplications"]);
  const sources = toStringList(pickFirst(doc, ["appendix_sources", "appendixSources"]));

  // Any non-metadata key that isn't one of the known sections → Additional Findings.
  const additional: Record<string, any> = {};
  for (const [k, v] of Object.entries(doc)) {
    if (KNOWN_PERSONA_INTELLIGENCE_KEYS.has(k)) continue;
    if (k.endsWith("Steps") || k.endsWith("Findings") || k.endsWith("Profiles") || k.endsWith("Implications") || k.endsWith("Sources") || k.endsWith("Summary")) continue;
    if (v === null || v === undefined || v === "") continue;
    additional[k] = v;
  }

  return (
    <div className="space-y-4" data-testid="persona-intelligence-document">
      {/* 1. Executive Summary */}
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4" data-testid="pi-executive-summary">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary mb-2">
          <FileText className="h-4 w-4" /> Executive Summary
        </div>
        {execSummary ? (
          <p className="text-[15px] whitespace-pre-wrap leading-relaxed text-foreground">{String(execSummary)}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">No executive summary returned.</p>
        )}
      </div>

      {/* 2. Recommended Next Steps */}
      <Section icon={ListChecks} label="Recommended Next Steps">
        {nextSteps.length > 0 ? (
          <div className="grid gap-2 sm:grid-cols-2" data-testid="pi-next-steps">
            {nextSteps.map((step, i) => (
              <div key={i} className="flex items-start gap-2.5 rounded-lg border bg-card p-3" data-testid={`pi-next-step-${i}`}>
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                  {i + 1}
                </div>
                <p className="text-sm leading-snug">{step}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No recommended next steps returned.</p>
        )}
      </Section>

      {/* 3. Research Findings by Topic */}
      <Section icon={Microscope} label="Research Findings by Topic">
        {findings.length > 0 ? (
          <div className="space-y-2" data-testid="pi-consolidated-findings">
            {findings.map((f, i) => {
              if (!isPlainObject(f)) {
                return (
                  <div key={i} className="rounded-lg border bg-card p-3 text-sm" data-testid={`pi-finding-${i}`}>
                    <ScalarValue value={f} />
                  </div>
                );
              }
              const topicId = pickFirst(f, ["topic_id", "topicId", "id", "number"]);
              const topic = pickFirst(f, ["topic", "title", "name"]);
              const synthesis = pickFirst(f, ["synthesis", "finding", "summary", "detail"]);
              const fConsumed = ["topic_id", "topicId", "id", "number", "topic", "title", "name", "synthesis", "finding", "summary", "detail"];
              return (
                <div key={i} className="rounded-lg border bg-card p-3 space-y-1.5" data-testid={`pi-finding-${i}`}>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">Topic {String(topicId ?? i + 1)}</Badge>
                    {topic && <p className="text-sm font-semibold leading-snug">{String(topic)}</p>}
                  </div>
                  {synthesis && <p className="text-sm whitespace-pre-wrap leading-snug text-muted-foreground">{String(synthesis)}</p>}
                  <RestObject obj={f} omit={fConsumed} />
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No consolidated research findings returned.</p>
        )}
      </Section>

      {/* 4. Persona Facets (audience_profiles) */}
      <Section icon={Users} label="Persona Facets">
        <div className="flex items-start gap-1.5 rounded-md bg-muted/40 p-2 text-[11px] text-muted-foreground" data-testid="pi-facets-help">
          <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>{PERSONA_FACETS_HELP_TEXT}</span>
        </div>
        {facets.length > 0 ? (
          <div className="space-y-2 mt-2" data-testid="pi-persona-facets">
            {facets.map((f, i) => {
              if (!isPlainObject(f)) {
                return (
                  <div key={i} className="rounded-lg border bg-card p-3 text-sm" data-testid={`pi-facet-${i}`}>
                    <ScalarValue value={f} />
                  </div>
                );
              }
              const name = pickFirst(f, ["facet_name", "facetName", "name", "title"]);
              const description = pickFirst(f, ["description", "summary", "detail"]);
              const relevance = pickFirst(f, ["commercial_relevance", "commercialRelevance", "relevance"]);
              const fConsumed = ["facet_name", "facetName", "name", "title", "description", "summary", "detail", "commercial_relevance", "commercialRelevance", "relevance"];
              return (
                <div key={i} className="rounded-lg border bg-card p-3 space-y-1.5" data-testid={`pi-facet-${i}`}>
                  <div className="flex items-center gap-2">
                    {name && <p className="text-sm font-semibold leading-snug">{String(name)}</p>}
                    <Badge variant="outline" className="text-[10px] ml-auto">Decision mode</Badge>
                  </div>
                  {description && <p className="text-sm leading-snug text-muted-foreground">{String(description)}</p>}
                  {relevance && (
                    <div className="rounded-md bg-primary/5 p-2 text-xs">
                      <span className="font-semibold text-foreground">Commercial relevance: </span>
                      <span className="text-muted-foreground">{String(relevance)}</span>
                    </div>
                  )}
                  <RestObject obj={f} omit={fConsumed} />
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic mt-2">No persona facets returned.</p>
        )}
      </Section>

      {/* 5. Messaging Implications */}
      <Section icon={MessageSquare} label="Messaging Implications">
        {isPlainObject(messaging) ? (
          (() => {
            const tone = pickFirst(messaging, ["recommended_tone", "recommendedTone", "tone"]);
            const foreground = toStringList(pickFirst(messaging, ["themes_to_foreground", "themesToForeground", "foreground"]));
            const avoid = toStringList(pickFirst(messaging, ["themes_to_avoid", "themesToAvoid", "avoid"]));
            const mConsumed = ["recommended_tone", "recommendedTone", "tone", "themes_to_foreground", "themesToForeground", "foreground", "themes_to_avoid", "themesToAvoid", "avoid"];
            return (
              <div className="space-y-2.5" data-testid="pi-messaging-implications">
                {tone && (
                  <div className="rounded-lg border bg-card p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Recommended Tone</p>
                    <p className="text-sm leading-snug">{String(tone)}</p>
                  </div>
                )}
                {foreground.length > 0 && (
                  <div>
                    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1.5">
                      <ThumbsUp className="h-3.5 w-3.5" /> Themes to Foreground
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-testid="pi-themes-foreground">
                      {foreground.map((t, i) => (
                        <span key={i} className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-800 dark:text-emerald-200" data-testid={`pi-theme-foreground-${i}`}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {avoid.length > 0 && (
                  <div>
                    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1.5">
                      <Ban className="h-3.5 w-3.5" /> Themes to Avoid
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-testid="pi-themes-avoid">
                      {avoid.map((t, i) => (
                        <span key={i} className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-800 dark:text-amber-200" data-testid={`pi-theme-avoid-${i}`}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <RestObject obj={messaging} omit={mConsumed} />
              </div>
            );
          })()
        ) : messaging ? (
          <FallbackOutput data={messaging} />
        ) : (
          <p className="text-xs text-muted-foreground italic">No messaging implications returned.</p>
        )}
      </Section>

      {/* Fallback: any additional non-metadata fields, before the appendix. */}
      {Object.keys(additional).length > 0 && (
        <Section icon={Sparkles} label="Additional Findings">
          <div className="rounded-lg border bg-card p-3" data-testid="pi-additional-findings">
            <CleanObject obj={additional} />
          </div>
        </Section>
      )}

      {/* 6. Appendix: Research Sources (always last, collapsed by default) */}
      {sources.length > 0 && <PersonaIntelligenceAppendix sources={sources} />}
    </div>
  );
}

function FocusGroupModeratorRenderer({ data }: { data: any }) {
  if (!isPlainObject(data)) return <FallbackOutput data={data} />;
  const moderator = isPlainObject(data.moderator) ? data.moderator : data;
  const name = pickFirst(moderator, ["name"]);
  const role = pickFirst(moderator, ["role"]);
  const expertise = pickFirst(moderator, ["expertise"]);
  const objective = pickFirst(moderator, ["moderation_objective", "objective"]);
  const style = pickFirst(moderator, ["moderation_style", "style"]);
  const priorities = toStringList(pickFirst(moderator, ["testing_priorities"]));
  const guardrails = toStringList(pickFirst(moderator, ["discussion_guardrails"]));
  const consumed = [
    "name", "role", "expertise", "moderation_objective", "objective",
    "moderation_style", "style", "testing_priorities", "discussion_guardrails",
  ];
  return (
    <div className="space-y-3" data-testid="focus-group-moderator">
      <div className="rounded-lg border bg-card p-3 space-y-1.5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0">
            <UserRound className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight" data-testid="text-moderator-name">{name ? String(name) : "Focus Group Head"}</p>
            {role && <p className="text-[11px] text-muted-foreground">{String(role)}</p>}
          </div>
          <Badge variant="secondary" className="text-[10px] ml-auto">Moderator</Badge>
        </div>
        {expertise && <p className="text-xs"><span className="font-semibold text-foreground">Expertise: </span><span className="text-muted-foreground">{String(expertise)}</span></p>}
        {objective && <p className="text-xs"><span className="font-semibold text-foreground">Moderation objective: </span><span className="text-muted-foreground">{String(objective)}</span></p>}
        {style && <p className="text-xs"><span className="font-semibold text-foreground">Moderation style: </span><span className="text-muted-foreground">{String(style)}</span></p>}
      </div>
      {priorities.length > 0 && (
        <Section icon={Target} label="Testing Priorities">
          <div className="flex flex-wrap gap-1.5">
            {priorities.map((p, i) => (
              <span key={i} className="rounded-md border bg-muted/50 px-2 py-1 text-xs font-medium" data-testid={`fg-priority-${i}`}>{p}</span>
            ))}
          </div>
        </Section>
      )}
      {guardrails.length > 0 && (
        <Section icon={Ban} label="Discussion Guardrails">
          <div className="flex flex-wrap gap-1.5">
            {guardrails.map((g, i) => (
              <span key={i} className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-800 dark:text-amber-200" data-testid={`fg-guardrail-${i}`}>{g}</span>
            ))}
          </div>
        </Section>
      )}
      <RestObject obj={moderator} omit={consumed} />
    </div>
  );
}

function FocusGroupQuestionsRenderer({ data }: { data: any }) {
  if (!isPlainObject(data)) return <FallbackOutput data={data} />;
  const questions = firstArray(data, ["focus_group_questions", "questions"]) ?? [];
  const consumed = ["focus_group_questions", "questions"];
  return (
    <div className="space-y-3" data-testid="focus-group-questions">
      {questions.length !== 10 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5" data-testid="fg-questions-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-300">Expected 10 focus group questions; received {questions.length}.</p>
        </div>
      )}
      <div className="space-y-2">
        {questions.map((q, i) => {
          if (!isPlainObject(q)) {
            return (
              <div key={i} className="rounded-lg border bg-card p-3 text-sm" data-testid={`fg-question-${i}`}>
                <CleanValue value={q} />
              </div>
            );
          }
          const num = pickFirst(q, ["id", "number"]) ?? i + 1;
          const question = pickFirst(q, ["question"]);
          const purpose = pickFirst(q, ["purpose"]);
          const testArea = pickFirst(q, ["primary_test_area", "test_area"]);
          const facets = toStringList(pickFirst(q, ["target_facets"]));
          const listen = pickFirst(q, ["what_to_listen_for", "listen_for"]);
          const probe = pickFirst(q, ["follow_up_probe", "probe"]);
          const qConsumed = [
            "id", "number", "question", "purpose", "primary_test_area", "test_area",
            "target_facets", "what_to_listen_for", "listen_for", "follow_up_probe", "probe",
          ];
          return (
            <div key={i} className="rounded-lg border bg-card p-3 space-y-1.5" data-testid={`fg-question-${i}`}>
              <div className="flex items-start gap-2">
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">{String(num)}</div>
                <p className="text-sm font-semibold leading-snug flex-1">{question ? String(question) : "—"}</p>
                {testArea && <Badge variant="outline" className="text-[10px] flex-shrink-0">{String(testArea)}</Badge>}
              </div>
              {purpose && <p className="text-xs"><span className="font-semibold text-foreground">Purpose: </span><span className="text-muted-foreground">{String(purpose)}</span></p>}
              {facets.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {facets.map((f, fi) => (
                    <span key={fi} className="rounded-full border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground" data-testid={`fg-question-${i}-facet-${fi}`}>{f}</span>
                  ))}
                </div>
              )}
              {listen && <p className="text-xs"><span className="font-semibold text-foreground">What to listen for: </span><span className="text-muted-foreground">{String(listen)}</span></p>}
              {probe && <p className="text-xs"><span className="font-semibold text-foreground">Follow-up probe: </span><span className="text-muted-foreground">{String(probe)}</span></p>}
              <RestObject obj={q} omit={qConsumed} />
            </div>
          );
        })}
      </div>
      <RestObject obj={data} omit={consumed} />
    </div>
  );
}

// ─── Workflow 16: Persona Focus Group Test report ───────────────────────
const FG_SCORE_STYLES: Record<string, string> = {
  strong_positive: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  cautious_positive: "border-teal-500/30 bg-teal-500/10 text-teal-700 dark:text-teal-300",
  mixed: "border-border bg-muted/60 text-muted-foreground",
  skeptical: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  resistant: "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300",
};

function fgScoreLabel(label: any): string {
  return String(label ?? "").replace(/_/g, " ");
}

function FgEmpty({ message }: { message: string }) {
  return <p className="text-xs text-muted-foreground italic">{message}</p>;
}

function FgChips({ items, tone = "neutral" }: { items: any[]; tone?: "neutral" | "positive" | "warning" }) {
  const cls =
    tone === "positive"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : tone === "warning"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200"
        : "border-border bg-muted/50 text-muted-foreground";
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span key={i} className={`rounded-md border px-2 py-1 text-xs font-medium ${cls}`}>
          {typeof it === "string" ? it : <CleanValue value={it} />}
        </span>
      ))}
    </div>
  );
}

function FgCardList({ items, tone }: { items: any[]; tone?: "positive" | "warning" }) {
  const cls =
    tone === "positive"
      ? "border-emerald-500/30 bg-emerald-500/10"
      : tone === "warning"
        ? "border-amber-500/30 bg-amber-500/10"
        : "border bg-muted/30";
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <div key={i} className={`rounded-md border p-2.5 text-sm ${cls}`}>
          {typeof it === "string" ? <span className="whitespace-pre-wrap leading-snug">{it}</span> : <CleanValue value={it} />}
        </div>
      ))}
    </div>
  );
}

function FgParticipantCard({ p, index }: { p: any; index: number }) {
  if (!isPlainObject(p)) {
    return <div className="rounded-lg border bg-card p-3 text-sm"><CleanValue value={p} /></div>;
  }
  const name = pickFirst(p, ["participant_name", "name"]) ?? `Participant ${pickFirst(p, ["participant_id"]) ?? index + 1}`;
  const facet = pickFirst(p, ["facet_name"]);
  const mode = pickFirst(p, ["persona_mode"]);
  const concern = pickFirst(p, ["core_concern"]);
  const lens = pickFirst(p, ["decision_lens"]);
  const trigger = pickFirst(p, ["likely_conversion_trigger"]);
  const dealbreaker = pickFirst(p, ["likely_dealbreaker"]);
  const consumed = ["participant_id", "participant_name", "name", "facet_name", "persona_mode", "core_concern", "decision_lens", "likely_conversion_trigger", "likely_dealbreaker"];
  return (
    <div className="rounded-lg border bg-card p-3 space-y-1.5" data-testid={`fg-participant-${index}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold leading-tight">{String(name)}</p>
        {facet && <Badge variant="secondary" className="text-[10px]">{String(facet)}</Badge>}
        {mode && <Badge variant="outline" className="text-[10px]">{String(mode)}</Badge>}
      </div>
      {concern && <p className="text-xs"><span className="font-semibold text-foreground">Core concern: </span><span className="text-muted-foreground">{String(concern)}</span></p>}
      {lens && <p className="text-xs"><span className="font-semibold text-foreground">Decision lens: </span><span className="text-muted-foreground">{String(lens)}</span></p>}
      {trigger && <p className="text-xs"><span className="font-semibold text-foreground">Likely conversion trigger: </span><span className="text-muted-foreground">{String(trigger)}</span></p>}
      {dealbreaker && <p className="text-xs"><span className="font-semibold text-foreground">Likely dealbreaker: </span><span className="text-muted-foreground">{String(dealbreaker)}</span></p>}
      <RestObject obj={p} omit={consumed} />
    </div>
  );
}

function FgResponseCard({ r, index }: { r: any; index: number }) {
  if (!isPlainObject(r)) {
    return <div className="rounded-md border bg-muted/30 p-2.5 text-sm"><CleanValue value={r} /></div>;
  }
  const name = pickFirst(r, ["participant_name", "name"]) ?? `Participant ${pickFirst(r, ["participant_id"]) ?? index + 1}`;
  const facet = pickFirst(r, ["facet_name"]);
  const verbatim = pickFirst(r, ["verbatim_response"]);
  const interpretation = pickFirst(r, ["commercial_interpretation"]);
  const score = pickFirst(r, ["response_score"]);
  const scoreLabel = pickFirst(r, ["score_label"]);
  const rationale = pickFirst(r, ["score_rationale"]);
  const consumed = ["participant_id", "participant_name", "name", "facet_name", "verbatim_response", "commercial_interpretation", "response_score", "score_label", "score_rationale"];
  const badgeCls = FG_SCORE_STYLES[String(scoreLabel ?? "")] ?? FG_SCORE_STYLES.mixed;
  return (
    <div className="rounded-md border bg-muted/20 p-2.5 space-y-1.5" data-testid={`fg-response-${index}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs font-semibold">{String(name)}</p>
        {facet && <span className="text-[10px] text-muted-foreground">· {String(facet)}</span>}
        {(scoreLabel || score !== undefined) && (
          <span className={`ml-auto rounded-md border px-1.5 py-0.5 text-[10px] font-medium capitalize ${badgeCls}`}>
            {fgScoreLabel(scoreLabel)}{score !== undefined ? ` · ${String(score)}` : ""}
          </span>
        )}
      </div>
      {verbatim && (
        <p className="text-sm italic border-l-2 border-primary/40 pl-2.5 text-foreground/90">"{String(verbatim)}"</p>
      )}
      {interpretation && <p className="text-xs"><span className="font-semibold text-foreground">Commercial read: </span><span className="text-muted-foreground">{String(interpretation)}</span></p>}
      {rationale && <p className="text-[11px] text-muted-foreground">{String(rationale)}</p>}
      <RestObject obj={r} omit={consumed} />
    </div>
  );
}

function FgQuestionCard({ q, index, defaultOpen }: { q: any; index: number; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!isPlainObject(q)) {
    return <div className="rounded-lg border bg-card p-3 text-sm"><CleanValue value={q} /></div>;
  }
  const qid = pickFirst(q, ["question_id", "id"]) ?? index + 1;
  const question = pickFirst(q, ["question"]);
  const testArea = pickFirst(q, ["primary_test_area", "test_area"]);
  const intent = pickFirst(q, ["moderator_intent"]);
  const responses = firstArray(q, ["participant_responses"]) ?? [];
  const synthesis = pickFirst(q, ["moderator_synthesis"]);
  return (
    <div className="rounded-lg border bg-card" data-testid={`fg-question-${index}`}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex w-full items-start gap-2 p-3 text-left hover-elevate rounded-lg" data-testid={`fg-question-toggle-${index}`}>
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">{String(qid)}</div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-snug">{question ? String(question) : "—"}</p>
              {testArea && <Badge variant="outline" className="text-[10px] mt-1">{String(testArea)}</Badge>}
            </div>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="px-3 pb-3 space-y-2">
          {intent && <p className="text-xs"><span className="font-semibold text-foreground">Moderator intent: </span><span className="text-muted-foreground">{String(intent)}</span></p>}
          {responses.length > 0 ? (
            <div className="space-y-2">
              {responses.map((r, ri) => <FgResponseCard key={ri} r={r} index={ri} />)}
            </div>
          ) : <FgEmpty message="No participant responses returned." />}
          {synthesis && (
            <div className="rounded-md border border-primary/20 bg-primary/5 p-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Moderator synthesis</p>
              <p className="text-sm whitespace-pre-wrap leading-snug">{String(synthesis)}</p>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function FocusGroupTestRenderer({ data }: { data: any }) {
  if (!isPlainObject(data)) return <FallbackOutput data={data} />;
  const summary = pickFirst(data, ["focus_group_summary"]);
  const participants = firstArray(data, ["participants"]) ?? [];
  const discussion = firstArray(data, ["question_discussion"]) ?? [];
  const patterns = firstArray(data, ["cross_participant_patterns"]) ?? [];
  const positives = firstArray(data, ["strongest_positive_signals"]) ?? [];
  const objections = firstArray(data, ["strongest_objections"]) ?? [];
  const reuse = firstArray(data, ["language_to_reuse"]) ?? [];
  const avoid = firstArray(data, ["language_to_avoid"]) ?? [];
  const proof = firstArray(data, ["proof_requirements"]) ?? [];
  const conversion = firstArray(data, ["conversion_conditions"]) ?? [];
  const strategy = firstArray(data, ["recommended_strategy_changes"]) ?? [];
  const scoreObj = pickFirst(data, ["commercial_responsiveness_score"]);
  const consumed = [
    "focus_group_summary", "participants", "question_discussion", "cross_participant_patterns",
    "strongest_positive_signals", "strongest_objections", "language_to_reuse", "language_to_avoid",
    "proof_requirements", "conversion_conditions", "recommended_strategy_changes", "commercial_responsiveness_score",
  ];

  const score = isPlainObject(scoreObj) ? scoreObj : undefined;
  const scoreVal = score ? pickFirst(score, ["score"]) : undefined;
  const scoreLbl = score ? pickFirst(score, ["score_label"]) : undefined;
  const scoreRationale = score ? pickFirst(score, ["rationale"]) : undefined;
  const mainConv = score ? toStringList(pickFirst(score, ["main_conversion_conditions"])) : [];
  const mainRisks = score ? toStringList(pickFirst(score, ["main_risk_factors"])) : [];

  return (
    <div className="space-y-4" data-testid="focus-group-report">
      <p className="text-[11px] text-muted-foreground italic">
        This is a simulated qualitative test. It does not replace real customer research, but it helps stress-test the persona strategy before creative or offer development.
      </p>

      {/* 1. Focus Group Summary */}
      <Section icon={MessageSquare} label="Focus Group Summary">
        {summary ? (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
            <p className="text-sm whitespace-pre-wrap leading-snug">{String(summary)}</p>
          </div>
        ) : <FgEmpty message="No focus group summary returned." />}
      </Section>

      {/* 2. Simulated Participants */}
      <Section icon={Users} label={`Simulated Participants${participants.length ? ` (${participants.length})` : ""}`}>
        <p className="text-[11px] text-muted-foreground mb-2">
          Participants represent decision-mode facets within the selected persona, not separate audiences.
        </p>
        {participants.length > 0 ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {participants.map((p, i) => <FgParticipantCard key={i} p={p} index={i} />)}
          </div>
        ) : <FgEmpty message="No simulated participants returned." />}
      </Section>

      {/* 3. Discussion by Question */}
      <Section icon={MessageSquare} label={`Discussion by Question${discussion.length ? ` (${discussion.length})` : ""}`}>
        {discussion.length > 0 ? (
          <div className="space-y-2">
            {discussion.map((q, i) => <FgQuestionCard key={i} q={q} index={i} defaultOpen={i === 0} />)}
          </div>
        ) : <FgEmpty message="No question discussion returned." />}
      </Section>

      {/* 4. Cross-Participant Patterns */}
      {patterns.length > 0 && (
        <Section icon={Compass} label="Cross-Participant Patterns">
          <FgCardList items={patterns} />
        </Section>
      )}

      {/* 5. Strongest Positive Signals */}
      {positives.length > 0 && (
        <Section icon={ThumbsUp} label="Strongest Positive Signals">
          <FgCardList items={positives} tone="positive" />
        </Section>
      )}

      {/* 6. Strongest Objections */}
      {objections.length > 0 && (
        <Section icon={ShieldAlert} label="Strongest Objections">
          <FgCardList items={objections} tone="warning" />
        </Section>
      )}

      {/* 7. Language to Reuse */}
      {reuse.length > 0 && (
        <Section icon={Quote} label="Language to Reuse">
          <FgChips items={reuse} tone="positive" />
        </Section>
      )}

      {/* 8. Language to Avoid */}
      {avoid.length > 0 && (
        <Section icon={Ban} label="Language to Avoid">
          <FgChips items={avoid} tone="warning" />
        </Section>
      )}

      {/* 9. Proof Requirements */}
      {proof.length > 0 && (
        <Section icon={ListChecks} label="Proof Requirements">
          <FgCardList items={proof} />
        </Section>
      )}

      {/* 10. Conversion Conditions */}
      {conversion.length > 0 && (
        <Section icon={ListChecks} label="Conversion Conditions">
          <FgCardList items={conversion} />
        </Section>
      )}

      {/* 11. Recommended Strategy Changes */}
      {strategy.length > 0 && (
        <Section icon={Lightbulb} label="Recommended Strategy Changes">
          <div className="space-y-1.5">
            {strategy.map((s, i) => (
              <div key={i} className="flex items-start gap-2 rounded-md border bg-muted/30 p-2.5">
                <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">{i + 1}</div>
                <div className="text-sm min-w-0">{typeof s === "string" ? <span className="whitespace-pre-wrap leading-snug">{s}</span> : <CleanValue value={s} />}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 12. Commercial Responsiveness Score */}
      <Section icon={Target} label="Commercial Responsiveness Score">
        {score ? (
          <div className="rounded-lg border bg-card p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{scoreVal !== undefined ? String(scoreVal) : "—"}</span>
              <span className="text-xs text-muted-foreground">/ 5</span>
              {scoreLbl && <Badge variant="secondary" className="text-[10px] capitalize ml-1">{fgScoreLabel(scoreLbl)}</Badge>}
            </div>
            {scoreRationale && <p className="text-sm whitespace-pre-wrap leading-snug text-muted-foreground">{String(scoreRationale)}</p>}
            {mainConv.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Main conversion conditions</p>
                <FgChips items={mainConv} tone="positive" />
              </div>
            )}
            {mainRisks.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Main risk factors</p>
                <FgChips items={mainRisks} tone="warning" />
              </div>
            )}
          </div>
        ) : <FgEmpty message="No commercial responsiveness score returned." />}
      </Section>

      {/* Additional / unknown useful fields */}
      {(() => {
        const rest = <RestObject obj={data} omit={consumed} />;
        return rest ? (
          <Section icon={Info} label="Additional Focus Group Findings">{rest}</Section>
        ) : null;
      })()}
    </div>
  );
}

// ─── Workflow 17: Final Persona Brief ───────────────────────────────────
function fpbLabel(k: string): string {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fpbToText(value: any, indent = 0): string {
  const pad = "  ".repeat(indent);
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return `${pad}${value}`;
  }
  if (Array.isArray(value)) {
    return value
      .map((v) => (isPlainObject(v) || Array.isArray(v) ? fpbToText(v, indent) : `${pad}- ${v}`))
      .filter(Boolean)
      .join("\n");
  }
  if (isPlainObject(value)) {
    return Object.entries(value)
      .map(([k, v]) => {
        if (v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) return "";
        const label = fpbLabel(k);
        if (typeof v === "object") return `${pad}${label}:\n${fpbToText(v, indent + 1)}`;
        return `${pad}${label}: ${v}`;
      })
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

// Renders an array of objects as labeled cards: a bold primary line plus a set
// of labeled secondary rows; falls back to CleanValue for non-object items.
function FpbCards({
  items,
  primary,
  rows,
  numbered,
}: {
  items: any[];
  primary: string[];
  rows: { label: string; keys: string[] }[];
  numbered?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => {
        if (!isPlainObject(it)) {
          return (
            <div key={i} className="rounded-md border bg-muted/30 p-2.5 text-sm">
              {typeof it === "string" ? <span className="whitespace-pre-wrap leading-snug">{it}</span> : <CleanValue value={it} />}
            </div>
          );
        }
        const head = pickFirst(it, primary);
        const consumed = [...primary, ...rows.flatMap((r) => r.keys)];
        return (
          <div key={i} className="flex items-start gap-2 rounded-md border bg-card p-2.5">
            {numbered && (
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">{i + 1}</div>
            )}
            <div className="min-w-0 flex-1 space-y-1">
              {head && <p className="text-sm font-semibold leading-snug">{String(head)}</p>}
              {rows.map((r, ri) => {
                const v = pickFirst(it, r.keys);
                if (!v) return null;
                return (
                  <p key={ri} className="text-xs">
                    <span className="font-semibold text-foreground">{r.label}: </span>
                    <span className="text-muted-foreground">{typeof v === "string" ? v : <CleanValue value={v} />}</span>
                  </p>
                );
              })}
              <RestObject obj={it} omit={consumed} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Renders a strategy object (messaging/creative/channel/offer) as labeled
// blocks: arrays become chips, scalars become text, nested objects recurse.
function FpbStrategy({ obj }: { obj: any }) {
  if (!isPlainObject(obj)) {
    return obj ? <CleanValue value={obj} /> : <FgEmpty message="Not provided." />;
  }
  const entries = Object.entries(obj).filter(
    ([, v]) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0),
  );
  if (entries.length === 0) return <FgEmpty message="Not provided." />;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{fpbLabel(k)}</p>
          {Array.isArray(v) ? (
            <FgChips items={v} />
          ) : isPlainObject(v) ? (
            <CleanValue value={v} />
          ) : (
            <p className="text-sm whitespace-pre-wrap leading-snug">{String(v)}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function FinalPersonaBriefRenderer({ data }: { data: any }) {
  const { toast } = useToast();
  if (!isPlainObject(data)) return <FallbackOutput data={data} />;

  const title = pickFirst(data, ["brief_title", "title"]);
  const execSummary = pickFirst(data, ["executive_summary", "summary", "overview"]);
  const overview = pickFirst(data, ["persona_overview"]);
  const opportunity = pickFirst(data, ["commercial_opportunity"]);
  const motivations = firstArray(data, ["core_motivations"]) ?? [];
  const barriers = firstArray(data, ["primary_barriers"]) ?? [];
  const trust = firstArray(data, ["trust_and_proof_requirements", "trust_proof_requirements"]) ?? [];
  const messaging = pickFirst(data, ["messaging_strategy"]);
  const creative = pickFirst(data, ["creative_strategy"]);
  const channel = pickFirst(data, ["channel_strategy"]);
  const offer = pickFirst(data, ["offer_and_conversion_strategy", "offer_conversion_strategy"]);
  const facets = firstArray(data, ["persona_facets"]) ?? [];
  const takeaways = firstArray(data, ["focus_group_takeaways"]) ?? [];
  const actionPlan = firstArray(data, ["immediate_action_plan"]) ?? [];
  const caveats = firstArray(data, ["research_caveats"]) ?? [];
  const sourceNote = pickFirst(data, ["source_note"]);

  const consumed = [
    "brief_title", "title", "executive_summary", "summary", "overview", "persona_overview",
    "commercial_opportunity", "core_motivations", "primary_barriers", "trust_and_proof_requirements",
    "trust_proof_requirements", "messaging_strategy", "creative_strategy", "channel_strategy",
    "offer_and_conversion_strategy", "offer_conversion_strategy", "persona_facets",
    "focus_group_takeaways", "immediate_action_plan", "research_caveats", "source_note",
  ];

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast({ title: "Final brief JSON copied" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(fpbToText(data));
      toast({ title: "Final brief text copied" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const ov = isPlainObject(overview) ? overview : undefined;
  const op = isPlainObject(opportunity) ? opportunity : undefined;
  const triggerMoments = op ? toStringList(pickFirst(op, ["likely_trigger_moments"])) : [];

  return (
    <div className="space-y-4" data-testid="final-persona-brief">
      {/* Title + export actions */}
      <div className="rounded-lg border border-primary/20 bg-gradient-to-br from-primary/10 to-transparent p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-primary mb-1">Final Persona Brief</p>
            <p className="text-lg font-bold leading-tight" data-testid="text-brief-title">{title ? String(title) : "Final Persona Brief"}</p>
          </div>
          <div className="flex flex-shrink-0 gap-1.5">
            <Button size="sm" variant="outline" onClick={copyText} data-testid="button-copy-brief-text">
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Text
            </Button>
            <Button size="sm" variant="outline" onClick={copyJson} data-testid="button-copy-brief-json">
              <Code2 className="h-3.5 w-3.5 mr-1.5" /> Copy JSON
            </Button>
          </div>
        </div>
      </div>

      {/* 2. Executive Summary */}
      <Section icon={FileText} label="Executive Summary">
        {execSummary ? (
          <div className="rounded-lg border bg-card p-3">
            <p className="text-sm whitespace-pre-wrap leading-snug">{String(execSummary)}</p>
          </div>
        ) : <FgEmpty message="No executive summary returned." />}
      </Section>

      {/* 3. Persona Overview */}
      <Section icon={UserRound} label="Persona Overview">
        {ov ? (
          <div className="rounded-lg border bg-card p-3 space-y-1.5">
            {pickFirst(ov, ["persona_name", "name"]) && (
              <p className="text-sm font-semibold leading-tight">{String(pickFirst(ov, ["persona_name", "name"]))}</p>
            )}
            <FpbKVRows
              obj={ov}
              fields={[
                ["Archetype", ["archetype"]],
                ["Decision context", ["decision_context"]],
                ["Buying mindset", ["buying_mindset"]],
                ["Commercial readiness", ["commercial_readiness"]],
              ]}
              omit={["persona_name", "name"]}
            />
          </div>
        ) : <FgEmpty message="No persona overview returned." />}
      </Section>

      {/* 4. Commercial Opportunity */}
      <Section icon={Target} label="Commercial Opportunity">
        {op ? (
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-1.5">
            <FpbKVRows
              obj={op}
              fields={[
                ["Opportunity", ["opportunity_summary"]],
                ["Best-fit offer / product", ["best_fit_offer_or_product"]],
                ["Strategic priority", ["strategic_priority"]],
              ]}
              omit={["likely_trigger_moments"]}
            />
            {triggerMoments.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Likely trigger moments</p>
                <FgChips items={triggerMoments} tone="positive" />
              </div>
            )}
          </div>
        ) : <FgEmpty message="No commercial opportunity returned." />}
      </Section>

      {/* 5. Core Motivations */}
      {motivations.length > 0 && (
        <Section icon={ThumbsUp} label="Core Motivations">
          <FpbCards
            items={motivations}
            primary={["motivation", "name", "title"]}
            rows={[
              { label: "Why it matters", keys: ["why_it_matters", "why"] },
              { label: "Commercial implication", keys: ["commercial_implication", "implication"] },
            ]}
          />
        </Section>
      )}

      {/* 6. Primary Barriers */}
      {barriers.length > 0 && (
        <Section icon={ShieldAlert} label="Primary Barriers">
          <FpbCards
            items={barriers}
            primary={["barrier", "name", "title"]}
            rows={[
              { label: "Why it matters", keys: ["why_it_matters", "why"] },
              { label: "How to address", keys: ["how_to_address", "address", "mitigation"] },
            ]}
          />
        </Section>
      )}

      {/* 7. Trust & Proof Requirements */}
      {trust.length > 0 && (
        <Section icon={ListChecks} label="Trust & Proof Requirements">
          <FpbCards
            items={trust}
            primary={["proof_need", "requirement", "name", "title"]}
            rows={[
              { label: "Why it matters", keys: ["why_it_matters", "why"] },
              { label: "Recommended asset / action", keys: ["recommended_asset_or_action", "recommended_asset", "action"] },
            ]}
          />
        </Section>
      )}

      {/* 8. Messaging Strategy */}
      {messaging && (
        <Section icon={MessageSquare} label="Messaging Strategy">
          <FpbStrategy obj={messaging} />
        </Section>
      )}

      {/* 9. Creative Strategy */}
      {creative && (
        <Section icon={Sparkles} label="Creative Strategy">
          <FpbStrategy obj={creative} />
        </Section>
      )}

      {/* 10. Channel Strategy */}
      {channel && (
        <Section icon={Compass} label="Channel Strategy">
          <FpbStrategy obj={channel} />
        </Section>
      )}

      {/* 11. Offer & Conversion Strategy */}
      {offer && (
        <Section icon={Briefcase} label="Offer & Conversion Strategy">
          <FpbStrategy obj={offer} />
        </Section>
      )}

      {/* 12. Persona Facets */}
      {facets.length > 0 && (
        <Section icon={Users} label="Persona Facets">
          <p className="text-[11px] text-muted-foreground mb-2">
            Persona facets are decision modes within the selected persona, not separate audiences.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {facets.map((f, i) => {
              if (!isPlainObject(f)) return <div key={i} className="rounded-lg border bg-card p-3 text-sm"><CleanValue value={f} /></div>;
              const fname = pickFirst(f, ["facet_name", "name"]);
              const mode = pickFirst(f, ["decision_mode", "mode"]);
              const use = pickFirst(f, ["commercial_use", "use"]);
              return (
                <div key={i} className="rounded-lg border bg-card p-3 space-y-1 min-w-0" data-testid={`brief-facet-${i}`}>
                  {fname && <p className="text-sm font-semibold leading-tight break-words">{String(fname)}</p>}
                  {mode && (
                    <span className="inline-block max-w-full rounded-md bg-secondary px-2 py-0.5 text-[10px] font-medium text-secondary-foreground whitespace-normal break-words leading-snug">
                      {String(mode)}
                    </span>
                  )}
                  {use && <p className="text-xs text-muted-foreground break-words">{String(use)}</p>}
                  <RestObject obj={f} omit={["facet_name", "name", "decision_mode", "mode", "commercial_use", "use"]} />
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* 13. Focus Group Takeaways */}
      {takeaways.length > 0 && (
        <Section icon={Quote} label="Focus Group Takeaways">
          <FpbCards
            items={takeaways}
            primary={["takeaway", "name", "title"]}
            rows={[
              { label: "Implication", keys: ["implication"] },
              { label: "Recommended adjustment", keys: ["recommended_adjustment", "adjustment"] },
            ]}
          />
        </Section>
      )}

      {/* 14. Immediate Action Plan */}
      {actionPlan.length > 0 && (
        <Section icon={ListChecks} label="Immediate Action Plan">
          <FpbCards
            items={actionPlan}
            primary={["action", "name", "title", "step"]}
            rows={[
              { label: "Why", keys: ["why", "rationale"] },
              { label: "Owner", keys: ["owner"] },
              { label: "Priority", keys: ["priority"] },
            ]}
            numbered
          />
        </Section>
      )}

      {/* 15. Research Caveats */}
      {caveats.length > 0 && (
        <Section icon={AlertTriangle} label="Research Caveats">
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 mb-2">
            <p className="text-[11px] text-amber-800 dark:text-amber-200">
              Simulated focus group output does not replace real customer interviews. Validate key assumptions with live research before major spend.
            </p>
          </div>
          <FgCardList items={caveats} tone="warning" />
        </Section>
      )}

      {/* 16. Source Note */}
      {sourceNote && (
        <p className="text-[11px] text-muted-foreground italic border-t pt-2" data-testid="text-source-note">{String(sourceNote)}</p>
      )}

      {/* Additional / unknown useful fields */}
      {(() => {
        const rest = <RestObject obj={data} omit={consumed} />;
        return rest ? <Section icon={Info} label="Additional Brief Details">{rest}</Section> : null;
      })()}
    </div>
  );
}

// Small labeled key/value rows used inside the brief's profile cards.
function FpbKVRows({ obj, fields, omit }: { obj: any; fields: [string, string[]][]; omit?: string[] }) {
  const consumed = [...fields.flatMap(([, keys]) => keys), ...(omit ?? [])];
  return (
    <>
      {fields.map(([label, keys], i) => {
        const v = pickFirst(obj, keys);
        if (!v) return null;
        return (
          <p key={i} className="text-xs">
            <span className="font-semibold text-foreground">{label}: </span>
            <span className="text-muted-foreground">{typeof v === "string" ? v : <CleanValue value={v} />}</span>
          </p>
        );
      })}
      <RestObject obj={obj} omit={consumed} />
    </>
  );
}

type RenderCtx = {
  results: Record<string, any>;
  researchersControl?: ResearchersControl;
  researchControl?: ResearchControl;
  researchRunning?: boolean;
  running?: boolean;
};

const MODULE_RENDERERS: Record<string, (data: any, ctx: RenderCtx) => React.ReactNode> = {
  lead: (d) => <LeadStrategistRenderer data={d} />,
  topics: (d, ctx) => (
    <ResearchTopicsRenderer
      data={d}
      researchers={ctx.results?.researchers}
      control={ctx.researchersControl}
      research={ctx.results?.research}
      researchControl={ctx.researchControl}
      researchRunning={ctx.researchRunning}
    />
  ),
  research: (d, ctx) => (
    <SpecialistResearchRenderer data={d} topics={ctx.results?.topics} researchers={ctx.results?.researchers} running={ctx.running} />
  ),
  synthesis: (d) => <PersonaIntelligenceRenderer data={d} />,
  focusGroupModerator: (d) => <FocusGroupModeratorRenderer data={d} />,
  focusGroupQuestions: (d) => <FocusGroupQuestionsRenderer data={d} />,
  focusGroupTest: (d) => <FocusGroupTestRenderer data={d} />,
  finalPersonaBrief: (d) => <FinalPersonaBriefRenderer data={d} />,
};

// Defensive boundary around a module's structured renderer. Large or malformed
// results (especially Workflow 6) should never crash the whole page — on render
// failure we fall back to the raw clean JSON for that module.
class RenderErrorBoundary extends Component<
  { fallbackData: any; children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { fallbackData: any; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown) {
    console.error("Persona Research Lab render error", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="space-y-2" data-testid="render-fallback">
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Unable to render this result as a document. Showing the clean JSON below instead.
            </p>
          </div>
          <pre className="text-[11px] bg-muted rounded-md p-3 overflow-auto max-h-96 whitespace-pre-wrap break-words">
            {(() => {
              try {
                return JSON.stringify(this.props.fallbackData, null, 2);
              } catch {
                return "Result could not be displayed.";
              }
            })()}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function ModuleOutput({
  moduleKey,
  data,
  allResults,
  researchersControl,
  researchControl,
  researchRunning,
  running,
}: {
  moduleKey: string;
  data: any;
  allResults: Record<string, any>;
  researchersControl?: ResearchersControl;
  researchControl?: ResearchControl;
  researchRunning?: boolean;
  running?: boolean;
}) {
  const renderer = MODULE_RENDERERS[moduleKey];
  const content = renderer
    ? renderer(data, { results: allResults, researchersControl, researchControl, researchRunning, running })
    : <FallbackOutput data={data} />;
  return (
    <RenderErrorBoundary fallbackData={data}>
      {content}
    </RenderErrorBoundary>
  );
}


// ─── Page ───────────────────────────────────────────────────────────────
export default function PersonaResearchLabPage() {
  const { brandId, flowId } = useParams<{ brandId: string; flowId?: string }>();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // When editing a saved flow we start from a blank state and hydrate it from
  // the server once the flow loads. A brand-new flow resumes the localStorage
  // working draft as before.
  const [state, setState] = useState<LabState>(() =>
    flowId ? makeInitialState(brandId) : loadState(brandId),
  );

  // Tracks the saved flow currently being edited so Save updates (vs. creates).
  const [currentFlowId, setCurrentFlowId] = useState<string | null>(flowId ?? null);

  // Load a saved flow when opened with a flowId.
  const { data: savedFlow } = useQuery<PersonaLabFlow>({
    queryKey: ["/api/persona-lab/flows", flowId],
    enabled: !!flowId,
  });

  // Hydrate state from the saved flow exactly once after it loads.
  const hydratedFlowRef = useRef(false);
  useEffect(() => {
    if (!savedFlow || hydratedFlowRef.current) return;
    hydratedFlowRef.current = true;
    setCurrentFlowId(savedFlow.id);
    const saved = (savedFlow.state ?? {}) as Partial<LabState>;
    setState({
      selectedBrandId: brandId || "",
      selectedPersonaId: saved.selectedPersonaId ?? "",
      results: saved.results ?? {},
      rawResults: saved.rawResults ?? {},
      status: saved.status ?? {},
      errors: saved.errors ?? {},
    });
  }, [savedFlow, brandId]);

  // Brand is fixed by the route — no brand selector.
  const selectedBrandId = brandId;
  const { data: brandDetail } = useQuery<Brand>({
    queryKey: ["/api/brands", selectedBrandId],
    enabled: !!selectedBrandId,
  });
  const { data: personas } = useQuery<Persona[]>({
    queryKey: ["/api/brands", selectedBrandId, "personas"],
    enabled: !!selectedBrandId,
  });

  const { data: status } = useQuery<{
    simApiKeySet: boolean;
    modules: Record<string, { urlSet: boolean; configured: boolean }>;
  }>({ queryKey: ["/api/persona-research-lab/status"] });

  // Derived selected records.
  const selectedBrand = brandDetail || null;
  const selectedPersona = useMemo(
    () => personas?.find((p) => p.id === state.selectedPersonaId) || null,
    [personas, state.selectedPersonaId],
  );

  // Product/offer selection is not part of the Research Lab flow yet; the
  // compact product context is optional and stays null until it is added.
  const selectedProduct = null;

  // Compact contexts derived from the selected records. We keep the raw
  // selectedBrand/selectedPersona in state for display, but only send these
  // compact versions to Sim.
  const compactBrand = useMemo(() => buildCompactBrandContext(selectedBrand), [selectedBrand]);
  const compactPersona = useMemo(() => buildCompactPersonaContext(selectedPersona), [selectedPersona]);
  const compactProduct = useMemo(() => buildCompactProductContext(selectedProduct), [selectedProduct]);

  const brief = useMemo(
    () => buildCompactResearchBrief({ brand: selectedBrand, persona: selectedPersona, product: selectedProduct }),
    [selectedBrand, selectedPersona, selectedProduct],
  );

  const setupReady = !!selectedBrand && !!selectedPersona;

  // Persist after each state change (covers selection + runs). Only the
  // unsaved working draft is mirrored to localStorage — when editing a saved
  // flow, changes live in component state and are committed via Save so the
  // shared draft key is never clobbered by a loaded flow.
  const firstRender = useRef(true);

  // Monotonic token identifying the current research session. It is bumped
  // whenever the session is invalidated (Clear Session, persona switch) so that
  // an in-flight module run started under a prior session can detect, on
  // completion, that it has been superseded and must not write its (now stale)
  // result into the fresh session.
  const sessionTokenRef = useRef(0);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (flowId) return;
    try {
      // Persist only a localStorage-safe copy — raw Sim responses are reduced
      // to lightweight debug summaries so giant Workflow 6 envelopes never get
      // serialized into storage (which could freeze or crash the tab).
      localStorage.setItem(STORAGE_KEY, JSON.stringify(buildPersistableState(state)));
    } catch { /* quota / serialization — non-fatal */ }
  }, [state, flowId]);

  const runModule = useMutation<
    { result: any; raw: any },
    Error,
    { moduleKey: string; sessionToken: number }
  >({
    mutationFn: async ({ moduleKey, sessionToken }) => {
      const ctx: BuildContext = {
        brief,
        compactBrand,
        compactPersona,
        compactProduct,
        results: state.results,
      };

      // Module 4 runs one Sim call per matched topic/researcher pair, sending
      // a single compact research_topic + specialist_researcher each time.
      // Results are committed to state as each pair completes so cards render
      // progressively instead of waiting for all pairs to finish.
      if (moduleKey === "research") {
        const pairs = buildResearchPairs(state.results);
        if (!pairs.length) {
          throw new Error("No research topics found. Generate research topics first.");
        }

        // Slot per pair, populated as calls resolve. Pending slots render as
        // "Researching…" while the mutation is in flight.
        const slots: any[] = pairs.map((p) => ({ topic_id: p.topicId, insights: [], pending: true }));
        const raws: any[] = pairs.map(() => null);

        const commit = (final: boolean) => {
          const results = slots.map(({ pending, ...rest }) => (final ? rest : { ...rest, pending }));
          const completed = slots.filter((s) => !s.pending && !s.error).length;
          const failed = slots.filter((s) => !s.pending && s.error).length;
          const totalInsights = slots.reduce(
            (sum, s) => sum + (firstArray(s, INSIGHT_LIST_KEYS)?.length ?? 0),
            0,
          );
          const result = {
            specialist_research: results,
            summary: {
              expected_pairs: pairs.length,
              completed,
              failed,
              total_insights: totalInsights,
            },
          };
          // Skip the write if this run has been superseded (persona switch /
          // clear) so a late-resolving pair can't repopulate a fresh session.
          if (sessionToken === sessionTokenRef.current) {
            setState((s) => ({
              ...s,
              results: { ...s.results, research: result },
              rawResults: { ...s.rawResults, research: raws.slice() },
            }));
          }
          return result;
        };

        await Promise.allSettled(
          pairs.map(async (pair, i) => {
            const payload = buildSpecialistResearchPayload(ctx, pair);
            console.log("Specialist research payload before stringify", {
              topic_id: pair.topicId,
              topic: payload.research_topic?.topic,
              researcher_id: payload.specialist_researcher?.id,
              researcher_name: payload.specialist_researcher?.name,
              payloadSizeKb: getPayloadSizeKb(payload),
              payload,
            });
            console.log("Specialist research stringified input preview", {
              topic_id: pair.topicId,
              inputType: typeof JSON.stringify(payload),
              inputPreview: JSON.stringify(payload).slice(0, 500),
            });
            try {
              const data = await runModuleJob(selectedBrandId, { moduleKey, payload });
              raws[i] = data?.raw ?? null;
              const list = firstArray(data?.result, RESEARCH_RESULT_LIST_KEYS);
              let item = list && list.length === 1 ? list[0] : data?.result;
              if (isPlainObject(item) && pickFirst(item, RESEARCH_TOPIC_ID_KEYS) == null) {
                item = { topic_id: pair.topicId, ...item };
              }
              slots[i] = { ...(item ?? { topic_id: pair.topicId, insights: [] }), pending: false };
            } catch (err: any) {
              const message = String(err?.message ?? err);
              raws[i] = { topic_id: pair.topicId, error: message };
              slots[i] = { topic_id: pair.topicId, insights: [], error: message, pending: false };
            }
            // Commit incrementally so this card renders as soon as it lands.
            commit(false);
          }),
        );

        return { result: commit(true), raw: raws };
      }

      const mod = WORKFLOW_MODULES.find((m) => m.key === moduleKey)!;
      const payload = mod.buildPayload(ctx);
      console.log("Sim workflow payload", {
        moduleKey,
        payloadSizeKb: getPayloadSizeKb(payload),
        keys: Object.keys(payload || {}),
        payload,
      });
      if (moduleKey === "synthesis") {
        console.log("Starting Persona Intelligence synthesis", {
          payloadSizeKb: getPayloadSizeKb(payload),
          specialistResults: state.results.research?.summary,
        });
      }
      return runModuleJob(selectedBrandId, { moduleKey, payload });
    },
    onMutate: ({ moduleKey }) => {
      setState((s) => ({
        ...s,
        status: { ...s.status, [moduleKey]: "running" },
        errors: { ...s.errors, [moduleKey]: "" },
      }));
    },
    onSuccess: (data, { moduleKey, sessionToken }) => {
      // Ignore results from a run that belongs to a superseded session — the
      // user cleared the session or switched persona while this run was in
      // flight. Writing them now would repopulate a fresh session with stale
      // results (e.g. a previous persona's specialist researchers).
      if (sessionToken !== sessionTokenRef.current) return;
      // Workflow 6 (Persona Intelligence / synthesis): strip the heavy Sim
      // metadata (providerTiming.timeSegments[].assistantContent, etc.) from the
      // result the instant it returns, before it is stored or rendered. Other
      // modules use the shared deep strip.
      const HEAVY_MODULES = new Set(["synthesis", "focusGroupTest", "finalPersonaBrief"]);
      const clean =
        HEAVY_MODULES.has(moduleKey) ? stripSimMetadata(data.result) : stripMeta(data.result);
      // Workflow 6 (synthesis) raw envelopes can be huge — keep only a
      // lightweight debug summary in state (and therefore localStorage). Never
      // store the full raw response, providerTiming, timeSegments, or
      // assistantContent.
      const rawToStore = HEAVY_MODULES.has(moduleKey) ? buildRawDebugSummary(data.raw) : data.raw;
      if (moduleKey === "synthesis") {
        console.log("Persona Intelligence raw response received", {
          rawSizeKb: getPayloadSizeKb(data.raw),
          model: data.raw?.model,
          tokens: data.raw?.tokens,
          hasProviderTiming: Boolean(data.raw?.providerTiming),
        });
        console.log("Persona Intelligence clean result", {
          cleanSizeKb: getPayloadSizeKb(clean),
          keys: Object.keys(clean || {}),
        });
      }
      setState((s) => {
        const downstream = downstreamModuleKeys(moduleKey);
        const results = { ...s.results, [moduleKey]: clean };
        const rawResults = { ...s.rawResults, [moduleKey]: rawToStore };
        const statusNext: Record<string, ModuleStatus> = { ...s.status, [moduleKey]: "complete" };
        const errorsNext = { ...s.errors, [moduleKey]: "" };
        // (Re)running this module invalidates everything downstream of it so
        // stale results from a prior run don't linger (and re-match the new
        // inputs).
        for (const k of downstream) {
          delete results[k];
          delete rawResults[k];
          delete statusNext[k];
          delete errorsNext[k];
        }
        return { ...s, results, rawResults, status: statusNext, errors: errorsNext };
      });
      toast({ title: `${MODULE_TITLE[moduleKey]} complete` });
    },
    onError: (e, { moduleKey, sessionToken }) => {
      // Drop errors from a superseded session so they don't surface against a
      // fresh, unrelated session.
      if (sessionToken !== sessionTokenRef.current) return;
      setState((s) => ({
        ...s,
        status: { ...s.status, [moduleKey]: "error" },
        errors: { ...s.errors, [moduleKey]: e.message },
      }));
    },
  });

  // Per-module run state is derived from the authoritative status map, not the
  // shared mutation's isPending. With sequential/concurrent module runs (e.g.
  // moderator → questions), React Query's single-observer isPending can stay
  // true after a module's own run has already settled, which left completed
  // cards spinning and blocked downstream steps (e.g. "Run Focus Group Test").
  // status[key] is set to "running" in onMutate and cleared in
  // onSuccess/onError, so it is the reliable per-module source of truth.
  const isModuleRunning = (key: string) => (state.status[key] ?? "idle") === "running";
  const anyModuleRunning = Object.values(state.status).some((s) => s === "running");
  const completedCount = TIMELINE_MODULES.filter((m) => state.status[m.key] === "complete").length;

  // Control for the "Generate Specialist Researchers" action that now lives
  // inside the Research Topics step.
  const researchersControl: ResearchersControl = {
    status: state.status["researchers"] ?? "idle",
    error: state.errors["researchers"],
    running: isModuleRunning("researchers"),
    anyRunning: anyModuleRunning,
    configured: status?.modules?.["researchers"]?.configured ?? true,
    hasResult: state.results["researchers"] !== undefined,
    onGenerate: () => runModule.mutate({ moduleKey: "researchers", sessionToken: sessionTokenRef.current }),
  };

  // Control for the "Run Specialist Research" action that now lives inside the
  // Research Topics step. Findings render inline under each topic.
  const researchControl: ResearchControl = {
    status: state.status["research"] ?? "idle",
    error: state.errors["research"],
    running: isModuleRunning("research"),
    anyRunning: anyModuleRunning,
    configured: status?.modules?.["research"]?.configured ?? true,
    hasResult: state.results["research"] !== undefined,
    canRun: state.results["researchers"] !== undefined,
    onGenerate: () => runModule.mutate({ moduleKey: "research", sessionToken: sessionTokenRef.current }),
  };

  const handleSelectPersona = (id: string) => {
    if (id === state.selectedPersonaId) return;
    // Switching persona starts a fresh session: invalidate any in-flight module
    // run so its late onSuccess can't write into the new session.
    sessionTokenRef.current += 1;
    // The entire research pipeline (lead, topics, researchers, research, …)
    // is persona-specific, so switching to a different persona starts a fresh
    // session — clear all prior module results/status so nothing carries over
    // (e.g. a previous persona's specialist researchers re-attaching to new
    // topics).
    setState((s) => ({
      ...s,
      selectedPersonaId: id,
      results: {},
      rawResults: {},
      status: {},
      errors: {},
    }));
  };

  const handleClearSession = () => {
    if (!window.confirm("Clear this research session? Your selections and all module results will be removed. This cannot be undone.")) {
      return;
    }
    // Invalidate any in-flight module run so its late onSuccess can't write
    // stale results into the cleared session.
    sessionTokenRef.current += 1;
    setState(makeInitialState(brandId));
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* non-fatal */ }
    toast({ title: "Session cleared" });
  };

  // Save the current flow to the dashboard. Creates a new flow on first save,
  // or updates the existing one when editing a saved flow. Step/status are
  // denormalized from the full set of workflow modules so the dashboard can
  // show "Step X of 9" / "Final" without re-deriving them.
  const saveFlow = useMutation<PersonaLabFlow, Error, void>({
    mutationFn: async () => {
      const completedAll = WORKFLOW_MODULES.filter((m) => state.status[m.key] === "complete").length;
      const isFinal = state.status["finalPersonaBrief"] === "complete";
      const title =
        (selectedPersona?.name ? `${selectedPersona.name} research` : "Persona research") +
        (selectedBrand?.name ? ` — ${selectedBrand.name}` : "");
      const payload = {
        title: title.slice(0, 255),
        personaId: state.selectedPersonaId || null,
        personaName: selectedPersona?.name || null,
        currentStep: completedAll,
        status: isFinal ? "final" : "in_progress",
        state: buildPersistableState(state),
      };
      if (currentFlowId) {
        const res = await apiRequest("PATCH", `/api/persona-lab/flows/${currentFlowId}`, payload);
        return res.json();
      }
      const res = await apiRequest("POST", `/api/brands/${brandId}/persona-lab/flows`, payload);
      return res.json();
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "persona-lab", "flows"] });
      if (currentFlowId) {
        queryClient.invalidateQueries({ queryKey: ["/api/persona-lab/flows", currentFlowId] });
      } else {
        // A brand-new flow has been promoted to a saved snapshot; clear the
        // working draft so the dashboard won't also show a "resume" entry.
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* non-fatal */ }
      }
      setCurrentFlowId(row.id);
      toast({ title: "Flow saved" });
      navigate(`/brands/${brandId}/persona-intelligence`);
    },
    onError: (e) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const handleSaveFlow = () => {
    if (!setupReady) {
      toast({ title: "Select a persona first", variant: "destructive" });
      return;
    }
    saveFlow.mutate();
  };

  const fullExport = useMemo(
    () => ({
      brand: selectedBrand,
      persona: selectedPersona,
      research_brief: brief,
      results: state.results,
      rawResults: state.rawResults,
    }),
    [selectedBrand, selectedPersona, brief, state.results, state.rawResults],
  );

  const handleExport = () => {
    const name = (selectedBrand?.name || "persona-research").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    downloadJson(`${name || "persona-research"}-research.json`, fullExport);
  };

  const handleCopyFull = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(fullExport, null, 2));
      toast({ title: "Full research JSON copied" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-full bg-background">
      <div className="max-w-[1100px] mx-auto p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground flex-shrink-0">
              <FlaskConical className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">Persona Research Lab</h1>
              <p className="text-sm text-muted-foreground" data-testid="text-page-subtitle">
                Launch transparent, step-by-step persona research from your existing brand and persona intelligence
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/brands/${brandId}/persona-intelligence`)}
              data-testid="button-back-dashboard"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Dashboard
            </Button>
            <Button
              size="sm"
              onClick={handleSaveFlow}
              disabled={saveFlow.isPending}
              data-testid="button-save-flow"
            >
              {saveFlow.isPending
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Save className="h-4 w-4 mr-1.5" />}
              {currentFlowId ? "Save flow" : "Save flow"}
            </Button>
          </div>
        </div>

        {/* Sticky summary bar */}
        <SummaryBar
          completedCount={completedCount}
          total={TIMELINE_MODULES.length}
          brandName={selectedBrand?.name}
          personaName={selectedPersona?.name}
          onClear={handleClearSession}
          onExport={handleExport}
          onCopy={handleCopyFull}
        />

        {/* Persona Research Setup */}
        <PersonaResearchSetup
          personas={personas ?? []}
          selectedPersonaId={state.selectedPersonaId}
          selectedBrand={selectedBrand}
          selectedPersona={selectedPersona}
          brief={brief}
          onSelectPersona={handleSelectPersona}
        />

        {/* Module timeline */}
        <div className="space-y-0" data-testid="module-timeline">
          {TIMELINE_MODULES.map((mod, idx) => {
            // Progressive disclosure: a step is only revealed once every module
            // it depends on has produced a result, so the user sees one step at
            // a time instead of the whole pipeline loaded up front. (`requires`
            // for the first step is empty, so it is always visible.)
            const stepRevealed = mod.requires.every((r) => state.results[r] !== undefined);
            if (!stepRevealed) return null;
            const missingReqTitles = mod.requires
              .filter((r) => !state.results[r])
              .map((r) => MODULE_TITLE[r]);
            const blockedBySetup = !setupReady;
            const moduleConfigured = status?.modules?.[mod.key]?.configured ?? true;

            // Synthesis-specific gating + soft warnings. Allow synthesis with
            // partial specialist research, but block only when zero results are
            // complete. Surface completeness/insight-count warnings otherwise.
            let warnings: string[] | undefined;
            let extraBlocked = false;
            let extraBlockReason: string | undefined;
            if (mod.key === "synthesis") {
              const summary = state.results.research?.summary;
              const completed = summary?.completed ?? 0;
              const expected = summary?.expected_pairs ?? 10;
              const totalInsights = summary?.total_insights ?? 0;
              const hasResearch = state.results.research !== undefined;
              const w: string[] = [];
              if (hasResearch && completed < expected) {
                w.push(`Specialist research is incomplete: ${completed} / ${expected} complete.`);
              }
              if (hasResearch && totalInsights < expected * RESEARCH_TARGET_INSIGHTS) {
                w.push(`Expected up to ${expected * RESEARCH_TARGET_INSIGHTS} insights; currently have ${totalInsights}.`);
              }
              warnings = w;
              if (hasResearch && completed === 0) {
                extraBlocked = true;
                extraBlockReason = "Complete at least one specialist research result before synthesizing.";
              }
            }
            if (mod.key === "focusGroupTest") {
              const facets = state.results.synthesis?.audience_profiles ?? [];
              if (state.results.synthesis !== undefined && (!Array.isArray(facets) || facets.length === 0)) {
                warnings = [
                  "No Persona Facets found. The simulator will derive participant modes from the Persona Intelligence Document.",
                ];
              }
            }
            return (
              <div key={mod.key} className="contents">
                {mod.groupHeader && (
                  <div className="pt-5 pb-1" data-testid={`group-header-${mod.key}`}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{mod.groupHeader}</p>
                  </div>
                )}
              <ModuleCard
                mod={mod}
                displayStep={idx + 1}
                status={state.status[mod.key] ?? "idle"}
                result={state.results[mod.key]}
                raw={state.rawResults[mod.key]}
                error={state.errors[mod.key]}
                missingReqTitles={missingReqTitles}
                blockedBySetup={blockedBySetup}
                configured={moduleConfigured}
                simApiKeySet={status?.simApiKeySet ?? true}
                running={isModuleRunning(mod.key)}
                anyRunning={anyModuleRunning}
                onRun={() => runModule.mutate({ moduleKey: mod.key, sessionToken: sessionTokenRef.current })}
                allResults={state.results}
                researchersControl={mod.key === "topics" ? researchersControl : undefined}
                researchControl={mod.key === "topics" ? researchControl : undefined}
                researchRunning={isModuleRunning("research")}
                warnings={warnings}
                extraBlocked={extraBlocked}
                extraBlockReason={extraBlockReason}
              />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Sticky summary bar ─────────────────────────────────────────────────
function SummaryBar({
  completedCount, total, brandName, personaName, onClear, onExport, onCopy,
}: {
  completedCount: number;
  total: number;
  brandName?: string;
  personaName?: string;
  onClear: () => void;
  onExport: () => void;
  onCopy: () => void;
}) {
  const summary = brandName
    ? `${brandName}${personaName ? ` · ${personaName}` : " · Select a persona to begin"}`
    : "Loading brand…";
  return (
    <div
      className="sticky top-2 z-20 rounded-xl border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-sm"
      data-testid="summary-bar"
    >
      <div className="p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1.5" data-testid="badge-progress">
            <CheckCircle2 className="h-3 w-3" />
            {completedCount} / {total} modules complete
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground truncate flex-1 min-w-[120px]" data-testid="text-setup-summary">
          {summary}
        </p>
        <div className="flex items-center gap-1.5 ml-auto">
          <Button variant="outline" size="sm" onClick={onCopy} data-testid="button-copy-full">
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy JSON
          </Button>
          <Button variant="outline" size="sm" onClick={onExport} data-testid="button-export-full">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export JSON
          </Button>
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={onClear} data-testid="button-clear-session">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear Session
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Persona Research Setup ─────────────────────────────────────────────
function personaAvatarUrl(p: any): string | null {
  return p?.imageUrl || p?.avatarUrl || p?.avatar || p?.image || null;
}

function PersonaResearchSetup({
  personas, selectedPersonaId,
  selectedBrand, selectedPersona, brief,
  onSelectPersona,
}: {
  personas: Persona[];
  selectedPersonaId: string;
  selectedBrand: any;
  selectedPersona: any;
  brief: any;
  onSelectPersona: (id: string) => void;
}) {
  const [showPayload, setShowPayload] = useState(false);

  const payload = useMemo(() => ({
    ...brief,
    brand: selectedBrand,
    persona: selectedPersona,
    research_brief: brief,
  }), [brief, selectedBrand, selectedPersona]);

  const brandLogo = selectedBrand?.logoUrl || selectedBrand?.adgenLogoUrl || null;

  return (
    <Card data-testid="panel-persona-research-setup">
      <CardContent className="p-4 space-y-3">
        {/* Setup row: brand (left) + persona dropdown (right) — even 50/50 split */}
        <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
          {/* Left — brand identity */}
          <div className="flex items-center gap-3 min-w-0 md:flex-1 md:basis-0" data-testid="bar-brand-context">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border bg-muted/30 overflow-hidden flex-shrink-0">
              <ImgWithFallback
                src={brandLogo}
                alt={selectedBrand?.name ?? "Brand logo"}
                className="h-full w-full object-contain"
                data-testid="img-brand-logo"
                fallback={<Building2 className="h-5 w-5 text-muted-foreground" />}
              />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none mb-1">Brand</p>
              <p className="text-sm font-semibold leading-tight truncate" data-testid="text-brand-name">
                {selectedBrand?.name ?? "Loading brand…"}
              </p>
              {selectedBrand?.category && (
                <p className="text-xs text-muted-foreground truncate mt-0.5" data-testid="text-brand-category">
                  {selectedBrand.category}
                </p>
              )}
              {selectedBrand?.positioning && (
                <p className="text-[11px] text-muted-foreground/80 line-clamp-1 leading-snug mt-0.5" data-testid="text-brand-positioning">
                  {selectedBrand.positioning}
                </p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px self-stretch bg-border" />

          {/* Right — persona dropdown */}
          <div className="flex flex-col justify-center w-full md:flex-1 md:basis-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
              <UserRound className="h-3 w-3" /> Persona
            </p>
            {personas.length === 0 ? (
              <p className="text-xs text-muted-foreground italic rounded-lg border border-dashed p-3 text-center" data-testid="text-no-personas">
                No saved personas for this brand yet.
              </p>
            ) : (
              <SearchableSelect
                key={selectedPersonaId || "none"}
                value={selectedPersonaId || ""}
                onValueChange={onSelectPersona}
                placeholder="Choose a persona…"
                testId="select-persona"
                triggerClassName="w-full h-11"
                options={personas.map((p) => {
                  const avatar = personaAvatarUrl(p);
                  return {
                    value: p.id,
                    label: p.name,
                    testId: `option-persona-${p.id}`,
                    node: (
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar className="h-7 w-7 flex-shrink-0">
                          {avatar ? <AvatarImage src={avatar} alt={p.name} /> : null}
                          <AvatarFallback className="bg-muted text-muted-foreground text-[10px]">
                            {(p.name || "?").slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{p.name}</span>
                      </div>
                    ),
                  };
                })}
              />
            )}
          </div>
        </div>

        {/* View Full Payload */}
        <Collapsible open={showPayload} onOpenChange={setShowPayload}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="button-toggle-payload">
              <Code2 className="h-3.5 w-3.5 mr-1.5" /> View Full Payload
              <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${showPayload ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <pre className="text-[11px] bg-muted rounded-md p-3 overflow-auto max-h-96 whitespace-pre-wrap break-words" data-testid="text-full-payload">
              {JSON.stringify(payload, null, 2)}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

// ─── Module card ────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: ModuleStatus }) {
  if (status === "complete") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 gap-1" data-testid="badge-status-complete">
        <CheckCircle2 className="h-3 w-3" /> Complete
      </Badge>
    );
  }
  if (status === "running") {
    return (
      <Badge className="bg-primary/15 text-primary border border-primary/30 gap-1" data-testid="badge-status-running">
        <Loader2 className="h-3 w-3 animate-spin" /> Running
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge className="bg-destructive/15 text-destructive border border-destructive/30 gap-1" data-testid="badge-status-error">
        <AlertTriangle className="h-3 w-3" /> Error
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1" data-testid="badge-status-idle">
      <CircleDashed className="h-3 w-3" /> Idle
    </Badge>
  );
}

// ─── Research Lead hiring animation ─────────────────────────────────────
// Plays while the "lead" module is running: a simple, high-tech "talent
// pipeline" — candidates ride a conveyor through Interview → Evaluate → Hire,
// with one getting selected (green check) each pass.
const TP_TOKEN_COUNT = 12;
const randInitial = () => {
  const A = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  return A[Math.floor(Math.random() * 26)] + A[Math.floor(Math.random() * 26)];
};
const randInitials = (n: number) => Array.from({ length: n }, randInitial);

// Role-specific hiring config. Each role gets its own recruiting stages (with
// captions) and a post-hire "provisioning" checklist, so the animation reads as
// purpose-built for that exact hire (research lead, focus group head, …).
type HiringStage = { icon: typeof UserSearch; label: string; caption: string };
type ProvisioningStep = { icon: typeof Server; label: string };

// Research lead: provision the agent with research tooling & data access.
const LEAD_HIRING_STAGES: HiringStage[] = [
  { icon: UserSearch, label: "Interviewing", caption: "Interviewing candidates for the role…" },
  { icon: ClipboardCheck, label: "Evaluating", caption: "Evaluating fit against the brand and persona…" },
  { icon: UserCheck, label: "Hiring", caption: "Making the offer and hiring your research lead…" },
];
const LEAD_PROVISIONING: ProvisioningStep[] = [
  { icon: Server, label: "Spinning up a secure virtual machine" },
  { icon: Globe, label: "Granting API access to Google, Reddit, X & YouTube" },
  { icon: Cpu, label: "Selecting the best-fit LLM models" },
  { icon: Database, label: "Loading brand doctrine & persona memory" },
  { icon: Wrench, label: "Calibrating research tools" },
  { icon: ShieldCheck, label: "Finalizing workspace & guardrails" },
];

// Focus group head: a behavioral scientist who will moderate the persona focus
// group — provision the moderation lab, panel, and discussion guide.
const FOCUS_GROUP_HEAD_HIRING_STAGES: HiringStage[] = [
  { icon: UserSearch, label: "Interviewing", caption: "Interviewing behavioral scientists for the moderator role…" },
  { icon: ClipboardCheck, label: "Evaluating", caption: "Evaluating moderation skill and persona-research fit…" },
  { icon: UserCheck, label: "Hiring", caption: "Making the offer and hiring your focus group head…" },
];
const FOCUS_GROUP_HEAD_PROVISIONING: ProvisioningStep[] = [
  { icon: Server, label: "Spinning up a secure moderation lab" },
  { icon: Users, label: "Assembling the persona-facet focus group panel" },
  { icon: Cpu, label: "Selecting the best-fit LLM models" },
  { icon: Database, label: "Loading brand doctrine & persona memory" },
  { icon: MessageSquare, label: "Drafting the discussion guide & testing priorities" },
  { icon: ShieldCheck, label: "Setting moderation guardrails & bias controls" },
];

// Generic recruiting/onboarding animation, driven by role-specific config so the
// same visual is reused across hires (research lead, focus group head, …).
function HiringAnimation({
  stages,
  provisioning,
  testId,
}: {
  stages: HiringStage[];
  provisioning: ProvisioningStep[];
  testId: string;
}) {
  // Initials shuffle continuously so it looks like we're cycling through a
  // huge pool of personalities and possibilities.
  const [belt, setBelt] = useState(() => randInitials(TP_TOKEN_COUNT));
  useEffect(() => {
    const id = setInterval(() => {
      setBelt((prev) => {
        const next = [...prev];
        const k = 1 + Math.floor(Math.random() * 2);
        for (let j = 0; j < k; j++) {
          next[Math.floor(Math.random() * next.length)] = randInitial();
        }
        return next;
      });
    }, 280);
    return () => clearInterval(id);
  }, []);

  // The run averages ~14s. Walk the stages so "Hiring" lands at ~13s.
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 5000);
    const t2 = setTimeout(() => setPhase(2), 13000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Once hired, provision the agent with its tools; cycle until the run loads.
  const [provStep, setProvStep] = useState(0);
  useEffect(() => {
    if (phase < 2) return;
    const id = setInterval(() => {
      setProvStep((s) => (s < provisioning.length - 1 ? s + 1 : s));
    }, 1800);
    return () => clearInterval(id);
  }, [phase, provisioning.length]);

  return (
    <div
      className="tp-wrap rounded-lg border bg-muted/20 p-3 space-y-2.5"
      data-testid={testId}
    >
      {/* Stages */}
      <div className="flex items-center">
        {stages.map((s, i) => {
          const done = i < phase;
          const active = i === phase;
          const isLast = i === stages.length - 1;
          return (
            <div
              key={s.label}
              className={`flex items-center min-w-0 ${isLast ? "flex-shrink-0" : "flex-1"}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="relative flex h-7 w-7 items-center justify-center flex-shrink-0">
                  {active && (
                    <span className="tp-stage-pulse absolute inset-0 rounded-full bg-primary/15" />
                  )}
                  <div
                    className={`relative flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${
                      done
                        ? "border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                        : active
                        ? "tp-glow border-primary/40 bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground/50"
                    }`}
                  >
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}
                  </div>
                </div>
                <span
                  className={`text-[11px] font-medium truncate transition-colors ${
                    done
                      ? "text-emerald-600 dark:text-emerald-400"
                      : active
                      ? "text-foreground"
                      : "text-muted-foreground/50"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < stages.length - 1 && (
                <div
                  className={`mx-2 h-px flex-1 min-w-3 transition-colors ${
                    i < phase ? "bg-emerald-500/50" : i === phase ? "tp-flow" : "bg-border"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Recruiting row — candidates pop into place, filling full width */}
      <div className="tp-belt-track relative overflow-hidden rounded-md border bg-background/70 px-3 py-2.5">
        <div className="flex items-center justify-between">
          {belt.map((c, i) => {
            const hired = i % 4 === 1;
            return (
              <div
                key={i}
                className="tp-pop relative flex-shrink-0"
                style={{ animationDelay: `${i * 0.22}s` }}
                aria-hidden="true"
              >
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full border text-[10px] font-semibold ${
                    hired
                      ? "tp-glow-emerald border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500/30"
                      : "border-border bg-muted text-muted-foreground"
                  }`}
                >
                  {c}
                </div>
                {hired && (
                  <span className="tp-hire-badge absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 text-white">
                    <CheckCircle2 className="h-2.5 w-2.5" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {/* Scanner sweep */}
        <div className="tp-scanner pointer-events-none absolute inset-y-0" />
      </div>

      {/* Caption / provisioning */}
      {phase < 2 ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
          {stages[phase].caption}
        </p>
      ) : (
        <div className="space-y-1.5">
          {provisioning.slice(0, provStep + 1).map((p, i) => {
            const completed = i < provStep;
            return (
              <div key={p.label} className="tp-prov-row flex items-center gap-2 text-xs">
                <span className="flex h-4 w-4 items-center justify-center flex-shrink-0">
                  {completed ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  )}
                </span>
                <p.icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/70" />
                <span className={completed ? "text-muted-foreground" : "text-foreground"}>
                  {p.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Plays while the "lead" module is running — hiring the research lead.
function ResearchLeadHiringAnimation() {
  return (
    <HiringAnimation
      stages={LEAD_HIRING_STAGES}
      provisioning={LEAD_PROVISIONING}
      testId="animation-lead-hiring"
    />
  );
}

// Plays while the "focusGroupModerator" module is running — hiring the focus
// group head (a behavioral scientist) who will moderate the persona focus group.
function FocusGroupHeadHiringAnimation() {
  return (
    <HiringAnimation
      stages={FOCUS_GROUP_HEAD_HIRING_STAGES}
      provisioning={FOCUS_GROUP_HEAD_PROVISIONING}
      testId="animation-focus-group-head-hiring"
    />
  );
}

// ─── Specialist researcher hiring (mini, per-topic) ─────────────────────
// A compact version of the Research Lead hiring animation that plays inside
// each individual research topic card while specialist researchers are being
// generated. It interviews a small shuffling pool of candidates, walks through
// Interview → Evaluate → Assign, then rests on "Assigning specialist" until the
// real assigned researcher arrives and replaces it.
function SpecialistHiringMini({ idx }: { idx: number }) {
  const stages = ["Interviewing candidates", "Evaluating fit", "Assigning specialist"];

  // Small candidate pool whose initials shuffle to imply a deep search.
  const [pool, setPool] = useState(() => randInitials(5));
  useEffect(() => {
    const id = setInterval(() => {
      setPool((prev) => {
        const next = [...prev];
        next[Math.floor(Math.random() * next.length)] = randInitial();
        return next;
      });
    }, 300 + (idx % 5) * 50);
    return () => clearInterval(id);
  }, [idx]);

  // Walk the stages, slightly staggered per card so they don't move in lockstep,
  // then hold on the final "Assigning specialist" stage until the result loads.
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const stagger = (idx % 4) * 350;
    const t1 = setTimeout(() => setPhase(1), 4000 + stagger);
    const t2 = setTimeout(() => setPhase(2), 9000 + stagger);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [idx]);

  return (
    <div
      className="rounded-md border border-dashed bg-muted/30 p-2 space-y-1.5"
      data-testid={`hiring-mini-${idx}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
          <UserSearch className="h-3 w-3 flex-shrink-0" /> Hiring specialist researcher
        </div>
        <div className="flex -space-x-1.5" aria-hidden="true">
          {pool.map((c, i) => {
            const pick = i === 1;
            return (
              <span
                key={i}
                className={`flex h-5 w-5 items-center justify-center rounded-full border text-[8px] font-semibold ${
                  pick
                    ? "tp-glow-emerald z-10 border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                    : "border-border bg-background text-muted-foreground"
                }`}
              >
                {c}
              </span>
            );
          })}
        </div>
      </div>
      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
        {stages[phase]}…
      </p>
      <div className="flex items-center gap-1">
        {stages.map((_, i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i < phase ? "bg-emerald-500/60" : i === phase ? "tp-flow" : "bg-border"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Research Topic exploration animation ───────────────────────────────
// Plays while the "topics" module is running (~30s). It frames the work as
// finding the gaps between what the model already knows about the persona and
// what it still needs to learn: a fixed knowledge map reveals which dimensions
// are covered vs. missing, then a synthesis checklist loops from ~28s onward.
const TT_DIMENSIONS = [
  { label: "Motivations", gap: true },
  { label: "Objections", gap: true },
  { label: "Trust proof", gap: false },
  { label: "Buy triggers", gap: true },
  { label: "Rituals", gap: false },
  { label: "Identity", gap: true },
  { label: "Pricing", gap: true },
  { label: "Info sources", gap: false },
];
const TT_FINALIZE = [
  { icon: Lightbulb, label: "Framing each gap as a research question" },
  { icon: Target, label: "Scoring gaps by impact on the persona" },
  { icon: ListChecks, label: "Matching evidence sources to each gap" },
  { icon: Compass, label: "Ranking topics by commercial value" },
  { icon: Sparkles, label: "Finalizing the research plan" },
];

function ResearchTopicExplorationAnimation() {
  const stages = [
    { icon: Database, label: "Model recall", caption: "Recalling what the model already knows about this persona…" },
    { icon: ScanSearch, label: "Gap analysis", caption: "Comparing against a full persona picture to find the gaps…" },
    { icon: Network, label: "Framing topics", caption: "Turning the biggest gaps into research topics…" },
  ];

  // Reveal the knowledge map one dimension at a time — covered vs. gap. Labels
  // are fixed (no shuffling), so the layout never jumps.
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setRevealed((r) => {
        if (r >= TT_DIMENSIONS.length) {
          clearInterval(id);
          return r;
        }
        return r + 1;
      });
    }, 450);
    return () => clearInterval(id);
  }, []);

  // The run averages ~30s. Walk stages, then enter a looping synthesis at ~28s.
  // phase 0..2 = stages, phase 3 = finalize loop.
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 10000);
    const t2 = setTimeout(() => setPhase(2), 20000);
    const t3 = setTimeout(() => setPhase(3), 28000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  // Finalize checklist: run through each step once, then rest on the last one
  // ("Finalizing the research plan") spinning until the run actually loads.
  const [finStep, setFinStep] = useState(0);
  useEffect(() => {
    if (phase < 3) return;
    const id = setInterval(() => {
      setFinStep((s) => {
        if (s >= TT_FINALIZE.length - 1) {
          clearInterval(id);
          return s;
        }
        return s + 1;
      });
    }, 1500);
    return () => clearInterval(id);
  }, [phase]);

  const activeStage = Math.min(phase, stages.length - 1);

  return (
    <div
      className="tp-wrap rounded-lg border bg-muted/20 p-3 space-y-2.5"
      data-testid="animation-topic-exploration"
    >
      {/* Stages */}
      <div className="flex items-center">
        {stages.map((s, i) => {
          const done = i < activeStage || (phase >= stages.length);
          const active = i === activeStage && phase < stages.length;
          const isLast = i === stages.length - 1;
          return (
            <div
              key={s.label}
              className={`flex items-center min-w-0 ${isLast ? "flex-shrink-0" : "flex-1"}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="relative flex h-7 w-7 items-center justify-center flex-shrink-0">
                  {active && (
                    <span className="tp-stage-pulse absolute inset-0 rounded-full bg-primary/15" />
                  )}
                  <div
                    className={`relative flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${
                      done
                        ? "border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                        : active
                        ? "tp-glow border-primary/40 bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground/50"
                    }`}
                  >
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}
                  </div>
                </div>
                <span
                  className={`text-[11px] font-medium truncate transition-colors ${
                    done
                      ? "text-emerald-600 dark:text-emerald-400"
                      : active
                      ? "text-foreground"
                      : "text-muted-foreground/50"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {!isLast && (
                <div
                  className={`mx-2 h-px flex-1 min-w-3 transition-colors ${
                    i < activeStage || phase >= stages.length
                      ? "bg-emerald-500/50"
                      : i === activeStage
                      ? "tp-flow"
                      : "bg-border"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Knowledge map — covered dimensions vs. gaps the model needs to research.
          Fixed grid + fixed labels = no layout jump; the scanner provides motion. */}
      <div className="tp-belt-track relative overflow-hidden rounded-md border bg-background/70 px-3 py-2.5">
        <div className="grid grid-cols-4 gap-2">
          {TT_DIMENSIONS.map((d, i) => {
            const shown = i < revealed;
            const isGap = d.gap;
            return (
              <div
                key={d.label}
                className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 transition-colors duration-500 ${
                  !shown
                    ? "border-dashed border-border bg-muted/40"
                    : isGap
                    ? "tp-gap border-amber-500/50 bg-amber-500/10"
                    : "border-emerald-500/40 bg-emerald-500/10"
                }`}
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center flex-shrink-0">
                  {!shown ? (
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground/50" />
                  ) : isGap ? (
                    <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                  )}
                </span>
                <span
                  className={`truncate text-[10px] font-medium ${
                    !shown
                      ? "text-muted-foreground/50"
                      : isGap
                      ? "text-amber-700 dark:text-amber-300"
                      : "text-emerald-700 dark:text-emerald-300"
                  }`}
                >
                  {d.label}
                </span>
              </div>
            );
          })}
        </div>
        {/* Scanner sweep */}
        <div className="tp-scanner pointer-events-none absolute inset-y-0" />
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Already in model knowledge
        </span>
        <span className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-amber-500" /> Gap — needs research
        </span>
      </div>

      {/* Caption / synthesis loop */}
      {phase < 3 ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
          {stages[activeStage].caption}
        </p>
      ) : (
        <div className="space-y-1.5">
          {TT_FINALIZE.map((p, i) => {
            const completed = i < finStep;
            const current = i === finStep;
            return (
              <div key={p.label} className="tp-prov-row flex items-center gap-2 text-xs">
                <span className="flex h-4 w-4 items-center justify-center flex-shrink-0">
                  {completed ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  ) : current ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  ) : (
                    <CircleDashed className="h-3.5 w-3.5 text-muted-foreground/40" />
                  )}
                </span>
                <p.icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/70" />
                <span className={completed ? "text-muted-foreground" : "text-foreground"}>
                  {p.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ModuleCard({
  mod, displayStep, status, result, raw, error, missingReqTitles,
  blockedBySetup, configured, simApiKeySet, running, anyRunning, onRun, allResults,
  researchersControl, researchControl, researchRunning,
  warnings, extraBlocked, extraBlockReason,
}: {
  mod: ModuleConfig;
  displayStep: number;
  status: ModuleStatus;
  result: any;
  raw: any;
  error?: string;
  missingReqTitles: string[];
  blockedBySetup: boolean;
  configured: boolean;
  simApiKeySet: boolean;
  running: boolean;
  anyRunning: boolean;
  onRun: () => void;
  allResults: Record<string, any>;
  researchersControl?: ResearchersControl;
  researchControl?: ResearchControl;
  researchRunning?: boolean;
  warnings?: string[];
  extraBlocked?: boolean;
  extraBlockReason?: string;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const blockedByReqs = missingReqTitles.length > 0;
  const blocked = blockedBySetup || blockedByReqs || !!extraBlocked;
  const disabled = running || anyRunning || blocked || !configured;
  const hasResult = result !== undefined;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(result ?? {}, null, 2));
    } catch { /* ignore */ }
  };

  return (
    <div className="mb-3" data-testid={`module-${mod.key}`}>
      {/* Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold flex-shrink-0 ${
                status === "complete"
                  ? "bg-emerald-500 text-white border-emerald-500"
                  : status === "running"
                    ? "bg-primary text-primary-foreground border-primary"
                    : status === "error"
                      ? "bg-destructive text-destructive-foreground border-destructive"
                      : "bg-muted text-muted-foreground"
              }`}
            >
              {status === "complete" ? <CheckCircle2 className="h-4 w-4" /> : displayStep}
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base">{mod.title}</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{mod.description}</p>
              {mod.helperText && (
                <p className="text-[11px] text-muted-foreground/80 italic mt-1" data-testid={`helper-${mod.key}`}>{mod.helperText}</p>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <StatusBadge status={status} />
              {hasResult && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={onRun}
                  disabled={disabled}
                  title="Regenerate"
                  aria-label="Regenerate"
                  data-testid={`button-regenerate-${mod.key}`}
                >
                  <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {/* Config warning */}
          {!configured && (
            <div className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-yellow-50 dark:bg-yellow-950/20 p-2.5">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-900 dark:text-yellow-200">
                This module's endpoint is not configured yet
                {!simApiKeySet ? " (and the Sim API key is missing)" : ""}. It will be available once the workflow URL is set.
              </p>
            </div>
          )}

          {/* Requirement status */}
          {blockedBySetup ? (
            <p className="text-xs text-muted-foreground" data-testid={`requires-${mod.key}`}>
              Select a persona to begin.
            </p>
          ) : blockedByReqs ? (
            <p className="text-xs text-muted-foreground" data-testid={`requires-${mod.key}`}>
              {mod.requiresHint ?? `Requires: ${missingReqTitles.join(", ")}`}
            </p>
          ) : extraBlocked && extraBlockReason ? (
            <p className="text-xs text-muted-foreground" data-testid={`requires-${mod.key}`}>
              {extraBlockReason}
            </p>
          ) : null}

          {/* Soft warnings (non-blocking) */}
          {!blocked && warnings && warnings.length > 0 && (
            <div className="space-y-1.5" data-testid={`warnings-${mod.key}`}>
              {warnings.map((w, wi) => (
                <div
                  key={wi}
                  className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-300"
                  data-testid={`warning-${mod.key}-${wi}`}
                >
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {/* Run action (hidden once a result exists — replaced by the
              compact regenerate icon in the card header) */}
          {!hasResult && (
            <div className="flex items-center gap-2">
              <Button
                onClick={onRun}
                disabled={disabled}
                data-testid={`button-run-${mod.key}`}
                variant="default"
              >
                {running ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Running…</>
                ) : (
                  <><Play className="h-4 w-4 mr-1.5" /> {mod.key === "lead" ? "Generate Research Lead" : mod.key === "focusGroupTest" ? "Run Focus Group Test" : mod.key === "finalPersonaBrief" ? "Generate Final Brief" : "Generate"}</>
                )}
              </Button>
            </div>
          )}

          {/* Loading message */}
          {running && (
            mod.key === "lead" ? (
              <ResearchLeadHiringAnimation />
            ) : mod.key === "focusGroupModerator" ? (
              <FocusGroupHeadHiringAnimation />
            ) : mod.key === "topics" ? (
              <ResearchTopicExplorationAnimation />
            ) : (
              <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`loading-${mod.key}`}>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {mod.loadingMessage}
              </div>
            )
          )}

          {/* Error */}
          {status === "error" && error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2.5" data-testid={`error-${mod.key}`}>
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <p className="text-xs text-destructive break-words">{error}</p>
            </div>
          )}

          {/* Output */}
          {hasResult && (
            <div className="space-y-3 pt-1" data-testid={`output-${mod.key}`}>
              <div className="rounded-lg border bg-muted/20 p-3">
                <ModuleOutput moduleKey={mod.key} data={result} allResults={allResults} researchersControl={researchersControl} researchControl={researchControl} researchRunning={researchRunning} running={running} />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleCopy} data-testid={`button-copy-${mod.key}`}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy JSON
                </Button>
                <Collapsible open={showRaw} onOpenChange={setShowRaw}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid={`button-toggle-raw-${mod.key}`}>
                      <Code2 className="h-3.5 w-3.5 mr-1.5" /> Raw JSON
                      <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${showRaw ? "rotate-180" : ""}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <pre className="text-[11px] bg-muted rounded-md p-3 overflow-auto max-h-96 whitespace-pre-wrap break-words">
                      {JSON.stringify(raw ?? result, null, 2)}
                    </pre>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
